import React, { useState, useEffect, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { tTax } from "../utils/taxonomyI18n";
import { useNavigate } from "react-router-dom";
import CreateVenueModal, { VENUE_TYPE_FAMILIES } from "../components/CreateVenueModal";
import ModernMapTiles from "../components/ModernMapTiles";
import PhotoLightbox, { GalleryPhoto } from "../components/PhotoLightbox";
import { formatScore } from "../utils/format";
import { Users } from "lucide-react";
import { FAST_COLOR } from "../styles/tokens";
import { FAST_CATEGORIES, FAST_CATEGORY_LABELS, FastCategoryKey, PinIcon, LIGHTNING_PATH, LightningIcon } from "../components/FastCategoryIcons";
import { FLAME_PATH, FIST_PATH, PowerIcon } from "../components/SkillIcons";
import VerifiedBadge from "../components/VerifiedBadge";
import FastCreateSheet from "../components/FastCreateSheet";
import { MOVE_MODE_ICONS, MoveMode } from "../components/move/MoveIcons";
import { moveModeLabel, MOVE_MODE_I18N_KEY } from "../components/move/MoveCard";
import MoveDetailModal from "../components/move/MoveDetailModal";
import { useIsMobile } from "../hooks/useIsMobile";

const DEFAULT_CENTER = { lat: 48.8566, lng: 2.3522 };

// Catégories réellement sélectionnables — "Tout" n'en est pas une : c'est simplement l'état où
// elles sont TOUTES actives (et le bouton qui y revient d'un geste). Les filtres se combinent
// librement ("Profils + Lives", "Lieux + Rituels"…), ils ne se remplacent plus l'un l'autre.
// "publications" a été scindé en "passions"/"powers" : une seule publication ne peut être QUE
// l'un ou l'autre (même entité de données, cf. publicationAccent plus bas) — le chip générique
// "Publications" ne disait rien de ce qu'on allait réellement voir sur la carte.
const CATEGORIES = [
  { key: "members", label: "Membres" },
  { key: "communities", label: "Communautés" },
  { key: "rituals", label: "Rituels" },
  { key: "passions", label: "Passions" },
  { key: "powers", label: "Powers" },
  { key: "venues", label: "Lieux" },
  { key: "lives", label: "Lives" },
  { key: "fasts", label: "Fasts" },
  { key: "moves", label: "Move" },
];
// `label` reste le fallback français — la clé i18n réellement affichée est résolue au rendu via
// cette table, `lang` n'étant pas disponible à la définition (module-level) de CATEGORIES.
const CATEGORY_I18N_KEY: Record<string, string> = {
  members: "explore_tab_members", passions: "explore_tab_passions", powers: "explore_tab_powers",
  venues: "explore_tab_venues", rituals: "explore_tab_rituals", communities: "explore_tab_communities",
  lives: "explore_tab_lives", fasts: "explore_tab_fasts", moves: "explore_tab_moves",
};
const ALL_CATEGORY_KEYS = CATEGORIES.map(c => c.key);
// Clé de groupe de liste → type de contenu, pour réutiliser le même pictogramme partout.
const GROUP_TO_KIND: Record<string, string> = { profiles: "member", venues: "venue", rituals: "ritual", communities: "community", publications: "publication", lives: "live", fasts: "fast", moves: "move" };

// Temps relatif ("À l'instant", "3h", "2j") — même formulation que le fil (PostCard.tsx), pour
// que la fraîcheur d'un Live se lise partout de la même façon.
const timeAgo = (dateStr?: string) => {
  if (!dateStr) return "";
  const min = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (min < 1) return "À l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `il y a ${d}j`;
  const mo = Math.floor(d / 30);
  return mo < 12 ? `il y a ${mo} mois` : `il y a ${Math.floor(mo / 12)} an${Math.floor(mo / 12) > 1 ? "s" : ""}`;
};

// Compte la fréquence d'une passion/power donnée parmi les membres, du plus fréquent au moins
// fréquent — les chips proposées reflètent ce que les gens autour de soi proposent vraiment,
// plutôt qu'une liste figée qui peut n'avoir aucun rapport avec qui est réellement à proximité.
const rankBySkillFrequency = (members: any[], field: "passions" | "powers" | "bookable_powers"): string[] => {
  const counts: Record<string, number> = {};
  members.forEach(m => (m[field] || []).forEach((label: string) => { counts[label] = (counts[label] || 0) + 1; }));
  return Object.keys(counts).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b));
};

// Hauteur du tiroir replié, utilisée pour recentrer un pin sélectionné au-dessus de lui plutôt
// que pile derrière (sinon on sélectionne un pin qu'on ne voit plus). Un peu plus haute que la
// version d'origine (150) : les losanges (publication/FAST) débordent légèrement de leur boîte de
// base (accent/badge posé sur la pointe basse), 150 les rognait.
const PEEK_HEIGHT = 168;

// Recentre la carte en décalant volontairement le centre réel vers le bas, pour que le point
// visé apparaisse dans la moitié haute de l'écran — au-dessus du tiroir de découverte, jamais caché
// derrière.
function flyAboveSheet(map: L.Map, latlng: [number, number], zoom: number, bottomOffsetPx: number) {
  const targetPoint = map.project(L.latLng(latlng[0], latlng[1]), zoom);
  const shifted = targetPoint.add(L.point(0, bottomOffsetPx / 2));
  const newCenter = map.unproject(shifted, zoom);
  map.flyTo(newCenter, zoom, { duration: 0.55 });
}

// Efface la sélection quand on tape sur un point vide de la carte (pas sur un pin — Leaflet
// arrête déjà la propagation du clic d'un marker vers la carte, donc ça ne se déclenche jamais
// par erreur en tapant un pin). C'est ce qui permet aux pins sélectionnés de rétrécir "quand on
// tape ailleurs".
function MapClickHandler({ onClick }: { onClick: () => void }) {
  useMapEvents({ click: onClick });
  return null;
}

// Signale la position du centre de la carte après chaque déplacement (glisser/zoomer), pour
// proposer "Rechercher dans cette zone" — sans ça, la carte ne montrait jamais que les environs
// de l'utilisateur, impossible de découvrir qui se trouve dans une autre ville en s'y déplaçant.
function MapMoveHandler({ onMoveEnd }: { onMoveEnd: (center: { lat: number; lng: number }) => void }) {
  useMapEvents({
    moveend: e => {
      const c = e.target.getCenter();
      onMoveEnd({ lat: c.lat, lng: c.lng });
    },
  });
  return null;
}

// PROFONDEUR / PRÉSENCE — la carte doit être SOBRE ET LISIBLE de loin, et se densifier
// sensiblement quand on s'approche, comme si le contenu remontait vers soi. Trois paliers
// suffisent : loin (rien du tout), intermédiaire, proche. On ne signale que les CHANGEMENTS de
// palier — un zoomend qui reste dans le même palier ne provoque aucun rendu.
type ZoomBucket = "far" | "mid" | "close";
const zoomBucketOf = (z: number): ZoomBucket => (z >= 16 ? "close" : z >= 13 ? "mid" : "far");
// react-leaflet's <MapContainer> capture sa prop `className` dans un useState() initial et ne
// la remet JAMAIS à jour ensuite (voir MapContainer.js : `const [props] = useState({ className,
// ... })`, jamais réassigné) — passer une classe dynamique comme prop n'a donc AUCUN effet après
// le premier rendu (vérifié en direct : la carte restait bloquée sur "map-zoom-far" même au zoom
// maximal). On applique donc la classe de palier directement sur le vrai conteneur DOM de
// Leaflet (map.getContainer()), la seule façon fiable de la faire réagir à chaque zoomend.
const ZOOM_BUCKET_CLASSES = ["map-zoom-far", "map-zoom-mid", "map-zoom-close"];
function MapDepthHandler({ onBucket }: { onBucket: (b: ZoomBucket) => void }) {
  const applyBucket = (m: L.Map, b: ZoomBucket) => {
    const el = m.getContainer();
    el.classList.remove(...ZOOM_BUCKET_CLASSES);
    el.classList.add(`map-zoom-${b}`);
    onBucket(b);
  };
  const map = useMapEvents({ zoomend: () => applyBucket(map, zoomBucketOf(map.getZoom())) });
  useEffect(() => { applyBucket(map, zoomBucketOf(map.getZoom())); }, [map]);
  return null;
}

// Distance en km entre deux points (Haversine) — sert à décider si la carte s'est assez
// déplacée pour proposer une nouvelle recherche, plutôt qu'à chaque pixel de glissement.
function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

// Un profil compte plus au fur et à mesure que la zone se densifie : on réduit la taille de base
// et on regroupe plus agressivement, pour que la carte reste lisible même avec des centaines,
// voire des milliers de profils autour de soi.
function scaledMemberSize(count: number, isMobile: boolean) {
  const base = isMobile ? 48 : 44;
  if (count > 300) return Math.max(base - 16, 28);
  if (count > 100) return Math.max(base - 10, 34);
  if (count > 30) return Math.max(base - 6, 40);
  return base;
}
function scaledClusterRadius(count: number, base: number) {
  if (count > 300) return base + 30;
  if (count > 100) return base + 15;
  return base;
}

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

// Pictogrammes monochromes, tracés en ligne comme partout ailleurs dans l'app (jamais d'emoji
// sur la carte) : ils ne servent QUE de repli quand un contenu n'a pas de photo — un cercle ou
// un carré vide sur la carte ne dirait rien de ce qu'il représente.
const GLYPH_PATHS: Record<string, string> = {
  member: `<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7"/>`,
  venue: `<path d="M3 10.5 12 3l9 7.5"/><path d="M5.5 9.8V20h13V9.8"/><path d="M10 20v-5.5h4V20"/>`,
  ritual: `<path d="M17 2.5 20.5 6 17 9.5"/><path d="M3.5 11V9a3 3 0 0 1 3-3h14"/><path d="M7 21.5 3.5 18 7 14.5"/><path d="M20.5 13v2a3 3 0 0 1-3 3h-14"/>`,
  community: `<circle cx="9" cy="8" r="3.2"/><path d="M2.5 20c0-3.6 2.9-5.8 6.5-5.8s6.5 2.2 6.5 5.8"/><path d="M16 5.2a3.2 3.2 0 0 1 0 6"/><path d="M17.5 14.6c2.4.6 4 2.4 4 5.4"/>`,
  publication: `<path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>`,
  live: `<polygon points="7,4 19,12 7,20" fill="currentColor" stroke="none"/>`,
  // Accents internes du losange — ils ne changent JAMAIS la silhouette (une publication reste un
  // losange), ils disent seulement laquelle des deux natures on regarde. Le bras est le signe des
  // Powers partout dans l'app (listes, fiches) ; la flamme lui répond pour les Passions — deux
  // tracés fins de même famille (voir SkillIcons.tsx, source unique de ces deux chemins).
  passion: FLAME_PATH,
  power: FIST_PATH,
  // Repris tel quel de l'ancienne icône Powers (même tracé) — voir FastCategoryIcons.tsx.
  fast: LIGHTNING_PATH,
  // Un tracé par mode MOVE (voiture/vélo/marche/trottinette) — mêmes chemins que
  // components/move/MoveIcons.tsx, "aplatis" ici (sans leur propre balise <svg>) pour rentrer
  // dans le gabarit glyphSvg ci-dessous, comme fast/passion/power juste au-dessus.
  move: `<path d="M19 17h2a1 1 0 0 0 1-1v-3c0-.9-.6-1.7-1.5-1.9L17 10s-1.3-1.4-2.2-2.3a2 2 0 0 0-1.4-.7H6c-.6 0-1.1.3-1.4.8L3 10.7A3.5 3.5 0 0 0 2 13v3c0 .6.4 1 1 1h2"/><path d="M9 17h6"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/>`,
  move_ride: `<path d="M19 17h2a1 1 0 0 0 1-1v-3c0-.9-.6-1.7-1.5-1.9L17 10s-1.3-1.4-2.2-2.3a2 2 0 0 0-1.4-.7H6c-.6 0-1.1.3-1.4.8L3 10.7A3.5 3.5 0 0 0 2 13v3c0 .6.4 1 1 1h2"/><path d="M9 17h6"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/>`,
  move_bike: `<circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><circle cx="15" cy="5" r="1.2" fill="currentColor" stroke="none"/><path d="M12 17.5V14l-3-3 4-3.2 2 3.2h3.5"/>`,
  move_walk: `<circle cx="12.5" cy="4.2" r="1.4" fill="currentColor" stroke="none"/><path d="M9.5 20.5 11 14l-2.5-1.5.7-4L13 8l2 2.5-1 2.5 2.5 3-1.5 4.5"/><path d="M6.5 7.5 11 9"/>`,
  move_scoot: `<circle cx="5" cy="19" r="2"/><circle cx="16.5" cy="19" r="2"/><path d="M7 19h7l1.5-11.5H18"/><path d="M12.5 7.5H18"/>`,
};
function glyphSvg(kind: string, size: number, color = "#0a0a0a") {
  const paths = GLYPH_PATHS[kind] || GLYPH_PATHS.publication;
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" color="${color}" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;
}
// Même jeu de pictogrammes, côté React cette fois (tiroir, fiches) — un contenu sans photo se
// reconnaît exactement pareil sur la carte et dans la liste.
const Glyph = ({ kind, size = 20, color = "#0a0a0a" }: { kind: string; size?: number; color?: string }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.9}
    strokeLinecap="round" strokeLinejoin="round" style={{ color, flexShrink: 0 }}
    dangerouslySetInnerHTML={{ __html: GLYPH_PATHS[kind] || GLYPH_PATHS.publication }}
  />
);

// "Événement", "Passion", "Power" : trois mots du vocabulaire produit pour UNE seule entité de
// données (une publication dont le `type` vaut "passion" ou "power"). D'où une seule et même
// silhouette — le losange — et un simple accent interne pour distinguer les deux natures.
const publicationAccent = (p: any): string | null =>
  p?.type === "power" ? "power" : p?.type === "passion" ? "passion" : null;

// Le losange, côté React (carrousel, listes, fiche) — pour qu'une publication ait la même
// silhouette dans le tiroir que sur la carte : on reconnaît sur la liste ce qu'on vient de
// toucher sur la carte, et inversement. Même construction que losangeIcon : cadre tourné à 45°,
// contenu contre-tourné pour rester droit.
const LosangeThumb = ({ url, side, accent = null, selected = false }: { url?: string | null; side: number; accent?: string | null; selected?: boolean }) => {
  const diag = Math.round(side * 1.4142);
  const badge = accent ? Math.max(13, Math.round(side * 0.4)) : 0;
  return (
    <div style={{ position: "relative", width: diag, height: diag, flexShrink: 0 }}>
      <div style={{ position: "absolute", top: "50%", left: "50%", width: side, height: side, transform: "translate(-50%,-50%)", filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.16))" }}>
        <div style={{ position: "absolute", inset: 0, transform: "rotate(45deg)", borderRadius: Math.max(4, Math.round(side * 0.16)), border: selected ? "2.5px solid #0a0a0a" : "2.5px solid white", boxSizing: "border-box", overflow: "hidden", backgroundColor: url ? "#0a0a0a" : "#F2F2F2" }}>
          {url
            ? <img src={url} alt="" style={{ position: "absolute", top: "50%", left: "50%", width: diag, height: diag, objectFit: "cover", transform: "translate(-50%,-50%) rotate(-45deg)" }} />
            : <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", transform: "rotate(-45deg)" }}><Glyph kind="publication" size={Math.round(side * 0.46)} /></span>}
        </div>
      </div>
      {accent && (
        <span style={{ position: "absolute", left: "50%", top: `calc(50% + ${Math.round(diag / 2 - badge * 0.34)}px)`, transform: "translate(-50%,-50%)", width: badge, height: badge, borderRadius: "50%", backgroundColor: "white", border: "1.5px solid #ECECEC", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }}>
          <Glyph kind={accent} size={Math.round(badge * 0.6)} />
        </span>
      )}
    </div>
  );
};

// Même construction que LosangeThumb (silhouette identique au marqueur de la carte), mais un
// habillage propre à FAST : un film gris léger sur la photo de l'organisateur pour que l'icône
// de catégorie (café, verre…) reste lisible par-dessus n'importe quelle photo, et le badge du
// bas toujours doré (jamais blanc/monochrome comme pour Passion/Power) — c'est la signature FAST.
const FastLosangeThumb = ({ url, side, category, selected = false }: { url?: string | null; side: number; category?: FastCategoryKey; selected?: boolean }) => {
  const diag = Math.round(side * 1.4142);
  const badge = Math.max(13, Math.round(side * 0.4));
  const CategoryIcon = FAST_CATEGORIES.find(c => c.key === category)?.Icon;
  return (
    <div style={{ position: "relative", width: diag, height: diag, flexShrink: 0 }}>
      <div style={{ position: "absolute", top: "50%", left: "50%", width: side, height: side, transform: "translate(-50%,-50%)", filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.16))" }}>
        <div style={{ position: "absolute", inset: 0, transform: "rotate(45deg)", borderRadius: Math.max(4, Math.round(side * 0.16)), border: `${selected ? 2.5 : 1.5}px solid ${FAST_COLOR}`, boxSizing: "border-box", overflow: "hidden", backgroundColor: "#0a0a0a" }}>
          {url && <img src={url} alt="" style={{ position: "absolute", top: "50%", left: "50%", width: diag, height: diag, objectFit: "cover", transform: "translate(-50%,-50%) rotate(-45deg)" }} />}
          <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(70,70,70,0.4)" }} />
          {CategoryIcon && (
            <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", transform: "rotate(-45deg)" }}>
              <CategoryIcon color="white" size={Math.round(side * 0.4)} />
            </span>
          )}
        </div>
      </div>
      <span style={{ position: "absolute", left: "50%", top: `calc(50% + ${Math.round(diag / 2 - badge * 0.34)}px)`, transform: "translate(-50%,-50%)", width: badge, height: badge, borderRadius: "50%", backgroundColor: "#0a0a0a", border: `1.5px solid ${FAST_COLOR}`, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <LightningIcon color={FAST_COLOR} size={Math.round(badge * 0.55)} />
      </span>
    </div>
  );
};

// Le carré, côté React (carrousel) — même logique que LosangeThumb : la miniature d'un lieu doit
// avoir la même silhouette dans le tiroir que sur la carte. Coins doux, non tourné : se distingue
// du cercle (profils/rituels/communautés) et du losange (publications) sans complexité ajoutée.
// `kind` pilote uniquement le pictogramme de repli (venue/ritual/community n'ont pas la même
// icône) — la silhouette carrée, elle, ne change jamais. Sert désormais aussi aux rituels et
// communautés créés avant l'obligation de photo (cover_url encore null pour ce contenu ancien).
const SquareThumb = ({ url, side, selected = false, kind = "venue" }: { url?: string | null; side: number; selected?: boolean; kind?: string }) => (
  <div style={{ width: side, height: side, borderRadius: Math.max(6, Math.round(side * 0.24)), overflow: "hidden", border: selected ? "2.5px solid #0a0a0a" : "2.5px solid white", boxShadow: "0 3px 8px rgba(0,0,0,0.16)", backgroundColor: url ? "#0a0a0a" : "#F2F2F2", flexShrink: 0 }}>
    {url
      ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Glyph kind={kind} size={Math.round(side * 0.42)} /></div>}
  </div>
);

// Marqueur unique de TOUS les contenus non-vidéo restants (profils, rituels, communautés) : un
// cercle contenant la vraie photo du contenu. Les lieux ont leur propre silhouette (carré, voir
// photoSquareIcon) et les publications la leur (losange, voir losangeIcon). Plus aucun pin
// cartographique classique (goutte, badge à pointe) — on montre le contenu lui-même.
// visualSize = taille visible du pin ; tapPadding = marge invisible ajoutée tout autour pour
// agrandir la zone tactile réelle sans changer l'apparence — sur mobile, un doigt est bien moins
// précis qu'un curseur de souris, la cible visuelle ne doit pas être la seule cible cliquable.
// PROFONDEUR — voir le bloc <style> plus bas. Chaque marqueur porte la classe `oltodo-marker` :
// c'est elle qui lui donne son halo ambiant (::before) et son ombre portée, dont l'intensité est
// pilotée uniquement par des variables CSS posées sur la carte selon le niveau de zoom. Aucun JS
// par image : on change une classe au zoomend, le CSS fait le reste.
// `--oltodo-delay` désarchonne les respirations des halos les uns des autres (au zoom le plus
// proche) pour que ça reste un frémissement organique, jamais un clignotement synchronisé.
const ambientDelay = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 997;
  return `${(h % 45) / 10}s`;
};
const markerShell = (total: number, seed: string, content: string) =>
  `<div class="oltodo-marker" style="width:${total}px;height:${total}px;--oltodo-delay:${ambientDelay(seed)}">${content}</div>`;

// `contentRingKind` : anneau de statut Vibe pour un profil (membre) — même convention que
// photoSquareIcon (un seul anneau, Real prioritaire), couleur noire (contraste garanti, web n'a
// pas de mode sombre) plutôt que le bleu réservé aux lieux.
function photoCircleIcon(kind: string, url: string | null | undefined, ringColor: string, visualSize: number, tapPadding = 0, seed = "", contentRingKind: "real" | "vibe" | null = null) {
  // object-fit:cover : la photo remplit le cercle en gardant ses proportions, quel que soit son
  // format d'origine (portrait, paysage, carré) — jamais d'étirement.
  const inner = url
    ? `<img src="${url}" style="width:100%;height:100%;object-fit:cover;display:block" />`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:white">${glyphSvg(kind, Math.round(visualSize * 0.46))}</div>`;
  let content = `<div style="width:${visualSize}px;height:${visualSize}px;border-radius:50%;border:3px solid ${ringColor};box-shadow:var(--oltodo-lift, 0 3px 10px rgba(0,0,0,0.25));overflow:hidden;background:white">${inner}</div>`;
  let ringedSize = visualSize;
  if (contentRingKind) {
    ringedSize += (RING_WIDTH + RING_GAP) * 2;
    content = `<div style="width:${ringedSize}px;height:${ringedSize}px;border-radius:50%;border:${RING_WIDTH}px solid ${contentRingKind === "real" ? REAL_RING_COLOR : "#0a0a0a"};display:flex;align-items:center;justify-content:center;">${content}</div>`;
  }
  const total = ringedSize + tapPadding * 2;
  return L.divIcon({
    html: markerShell(total, seed || String(url || kind), content),
    className: "",
    iconSize: [total, total],
    iconAnchor: [total / 2, total / 2],
  });
}

// CINQUIÈME SILHOUETTE — le CARRÉ (coins arrondis, non tourné), réservé aux Lieux : jusqu'ici les
// lieux partageaient le cercle des profils/rituels/communautés, ce qui les rendait indiscernables
// d'un profil au premier coup d'œil sur une carte dense. Un carré aux coins doux se distingue du
// cercle (profils/rituels/communautés) sans se confondre avec le losange à 45° (publications) ni
// le rectangle 16:10 (Lives) — même taille de référence que le cercle, juste sa géométrie change.
// Anneau "il se passe quelque chose ici" : bleu = une vibe active à ce lieu (moins de 24h),
// rouge = un Real actif — UN SEUL anneau à la fois, Real prioritaire (jamais deux concentriques :
// uniformisation demandée explicitement, cf. plan Explorer+Vibes). Ni l'un ni l'autre ne réutilise
// le noir déjà pris par "sélectionné"/"ami" sur ce même carré — voir has_vibe_content/
// has_real_content, calculés côté serveur sur GET /venues/:id et /venues/nearby.
const VIBE_RING_COLOR = "#3B82F6";
const REAL_RING_COLOR = "#FF3B30";
const RING_WIDTH = 3;
const RING_GAP = 2;

function photoSquareIcon(url: string | null | undefined, ringColor: string, visualSize: number, tapPadding = 0, seed = "", hasVibe = false, hasReal = false, kind = "venue", rating: number | null = null) {
  const radius = Math.max(6, Math.round(visualSize * 0.24));
  const inner = url
    ? `<img src="${url}" style="width:100%;height:100%;object-fit:cover;display:block" />`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:white">${glyphSvg(kind, Math.round(visualSize * 0.42))}</div>`;
  let content = `<div style="width:${visualSize}px;height:${visualSize}px;border-radius:${radius}px;border:3px solid ${ringColor};box-shadow:var(--oltodo-lift, 0 3px 10px rgba(0,0,0,0.25));overflow:hidden;background:white">${inner}</div>`;
  let ringedSize = visualSize;
  if (hasVibe || hasReal) {
    ringedSize += (RING_WIDTH + RING_GAP) * 2;
    content = `<div style="width:${ringedSize}px;height:${ringedSize}px;border-radius:${radius + RING_WIDTH + RING_GAP}px;border:${RING_WIDTH}px solid ${hasReal ? REAL_RING_COLOR : VIBE_RING_COLOR};display:flex;align-items:center;justify-content:center;">${content}</div>`;
  }
  // Score façon TheFork — pastille blanche accrochée au coin bas-droit, uniquement s'il existe au
  // moins un avis (average_rating est NULL en base tant que personne n'a noté ce lieu).
  if (rating != null && !isNaN(rating)) {
    const badgeH = Math.max(15, Math.round(visualSize * 0.32));
    content = `<div style="position:relative;width:${ringedSize}px;height:${ringedSize}px">${content}<span style="position:absolute;bottom:-4px;right:-4px;height:${badgeH}px;padding:0 5px;border-radius:${badgeH}px;background:white;border:1.5px solid #ECECEC;box-sizing:border-box;display:flex;align-items:center;gap:2px;box-shadow:0 1px 4px rgba(0,0,0,0.22);font:600 ${Math.round(badgeH * 0.58)}px -apple-system,sans-serif;color:#0a0a0a;white-space:nowrap">
      <svg width="${Math.round(badgeH * 0.5)}" height="${Math.round(badgeH * 0.5)}" viewBox="0 0 24 24" fill="#0a0a0a"><polygon points="12,2 15,9 22,9.5 16.5,14.5 18.5,22 12,17.5 5.5,22 7.5,14.5 2,9.5 9,9"/></svg>${rating.toFixed(1)}
    </span></div>`;
    ringedSize += 4;
  }
  const total = ringedSize + tapPadding * 2;
  return L.divIcon({
    html: markerShell(total, seed || String(url || "venue"), content),
    className: "",
    iconSize: [total, total],
    iconAnchor: [total / 2, total / 2],
  });
}

// Les Lives sont la seule silhouette RECTANGULAIRE (format vidéo, 16:10) de la carte : c'est ce
// qui fait qu'une vidéo se repère instantanément au milieu des cercles et des losanges, sans
// avoir à lire quoi que ce soit — la forme dit déjà "ça se regarde". L'aperçu est la première
// image de la vidéo elle-même (<video muted preload="metadata"> — exactement le procédé déjà
// utilisé par la galerie de Profile.tsx), surmontée du glyphe de lecture rouge, la couleur déjà
// associée au "(live)" dans PhotoLightbox.tsx.
const LIVE_RATIO = 1.6; // 16:10, assez large pour ne jamais être confondu avec un carré.
function liveRectIcon(url: string | null | undefined, ringColor: string, visualSize: number, tapPadding = 0, seed = "") {
  // visualSize reste la "taille de référence" partagée avec les autres marqueurs ; on en dérive
  // une largeur et une hauteur de surface comparable, pas un carré étiré.
  const w = Math.round(visualSize * 1.24);
  const h = Math.round(w / LIVE_RATIO);
  const totalW = w + tapPadding * 2;
  const totalH = Math.max(h, Math.round(visualSize * 0.9)) + tapPadding * 2;
  const radius = Math.max(5, Math.round(h * 0.2));
  const play = Math.round(h * 0.5);
  const inner = url
    ? `<video src="${url}" muted preload="metadata" playsinline style="width:100%;height:100%;object-fit:cover;display:block"></video>`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:white">${glyphSvg("live", Math.round(h * 0.5), "#e74c3c")}</div>`;
  return L.divIcon({
    html: `<div class="oltodo-marker" style="width:${totalW}px;height:${totalH}px;--oltodo-delay:${ambientDelay(seed || String(url || "live"))}">
      <div style="position:relative;width:${w}px;height:${h}px;border-radius:${radius}px;border:3px solid ${ringColor};box-shadow:var(--oltodo-lift, 0 3px 10px rgba(0,0,0,0.25));overflow:hidden;background:#0a0a0a;box-sizing:border-box">
        ${inner}
        <span style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center">
          <span style="width:${play}px;height:${play}px;border-radius:50%;background:#e74c3c;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,0.35)">
            <svg width="${Math.round(play * 0.5)}" height="${Math.round(play * 0.5)}" viewBox="0 0 24 24" fill="white"><polygon points="7,4 19,12 7,20" /></svg>
          </span>
        </span>
      </div>
    </div>`,
    className: "",
    iconSize: [totalW, totalH],
    iconAnchor: [totalW / 2, totalH / 2],
  });
}

// QUATRIÈME SILHOUETTE — le LOSANGE, réservé aux publications géolocalisées (Événement /
// Passion / Power : trois mots pour une seule et même entité côté données, une `publication`
// dont le `type` vaut "passion" ou "power"). Un cercle de plus ne se serait distingué de rien ;
// un carré appartient déjà aux Lives. Le losange se lit instantanément comme "autre chose", et
// sa pointe basse tombe pile sur le point géographique, ce qu'aucun cercle ne fait aussi bien.
// La photo de couverture remplit le losange : on tourne le CADRE de 45°, jamais la photo — elle
// est contre-tournée de -45° à l'intérieur pour rester droite, agrandie de √2 pour couvrir le
// carré tourné sans laisser d'angle vide. Sans couverture, on retombe sur EXACTEMENT la même
// convention que les cercles sans photo : le pictogramme monochrome de GLYPH_PATHS.
// accent ("passion" | "power" | null) : un tout petit pictogramme dans une pastille blanche à la
// pointe basse. Même forme, même couleur, même taille pour les deux — seul le signe change, ce
// qui suffit à savoir de quoi il s'agit sans lire une ligne de texte.
function losangeIcon(url: string | null | undefined, ringColor: string, visualSize: number, tapPadding = 0, accent: string | null = null, seed = "") {
  // Un losange "paraît" plus grand que sa surface réelle (l'œil lit sa diagonale) : on part donc
  // d'un côté un peu plus petit que le diamètre d'un cercle, sinon il écrase tout le reste.
  const side = Math.round(visualSize * 0.78);
  const diag = Math.round(side * 1.4142);
  const badge = accent ? Math.max(14, Math.round(side * 0.42)) : 0;
  const total = diag + Math.round(badge * 0.5) + tapPadding * 2;
  const radius = Math.max(4, Math.round(side * 0.16));
  const inner = url
    ? `<img src="${url}" style="position:absolute;top:50%;left:50%;width:${diag}px;height:${diag}px;object-fit:cover;transform:translate(-50%,-50%) rotate(-45deg);display:block" />`
    : `<span style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;transform:rotate(-45deg)">${glyphSvg("publication", Math.round(side * 0.46))}</span>`;
  const accentBadge = accent
    ? `<span style="position:absolute;left:50%;top:calc(50% + ${Math.round(diag / 2 - badge * 0.34)}px);transform:translate(-50%,-50%);width:${badge}px;height:${badge}px;border-radius:50%;background:white;border:1.5px solid #ECECEC;box-sizing:border-box;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,0.22)">${glyphSvg(accent, Math.round(badge * 0.6))}</span>`
    : "";
  return L.divIcon({
    // L'ombre est portée par une enveloppe NON tournée (drop-shadow suit la silhouette réelle du
    // losange) : posée sur le cadre tourné, elle serait tombée en biais.
    html: markerShell(total, seed || String(url || "publication"), `
      <span style="position:absolute;top:50%;left:50%;width:${side}px;height:${side}px;transform:translate(-50%,-50%);display:block;filter:drop-shadow(var(--oltodo-drop, 0 2px 5px rgba(0,0,0,0.26)))">
        <span style="position:absolute;inset:0;transform:rotate(45deg);border-radius:${radius}px;border:3px solid ${ringColor};box-sizing:border-box;overflow:hidden;background:white;display:block">${inner}</span>
      </span>${accentBadge}`),
    className: "",
    iconSize: [total, total],
    iconAnchor: [total / 2, total / 2],
  });
}

// FAST — même silhouette losange que les publications (pour rester dans le langage de formes
// déjà établi), mais un traitement délibérément différent : PAS de photo à l'intérieur, fond
// sombre + le même éclair que celui autrefois utilisé pour les Powers (mêmes proportions, même
// tracé), simplement recoloré en doré. Le doré reste une signature (contour fin, glyphe), jamais
// un remplissage — pas de gros aplat doré, pas de halo puissant, beaucoup d'espace négatif.
function fastLosangeIcon(visualSize: number, tapPadding = 0, seed = "", selected = false) {
  const side = Math.round(visualSize * 0.72);
  const diag = Math.round(side * 1.4142);
  const total = diag + tapPadding * 2;
  const radius = Math.max(4, Math.round(side * 0.16));
  const borderWidth = selected ? 2.5 : 1.5;
  const boltSize = Math.round(side * 0.44);
  return L.divIcon({
    html: markerShell(total, seed || "fast", `
      <span style="position:absolute;top:50%;left:50%;width:${side}px;height:${side}px;transform:translate(-50%,-50%);display:block;filter:drop-shadow(var(--oltodo-drop, 0 2px 5px rgba(0,0,0,0.35)))">
        <span style="position:absolute;inset:0;transform:rotate(45deg);border-radius:${radius}px;border:${borderWidth}px solid ${FAST_COLOR};box-sizing:border-box;background:#0a0a0a;display:flex;align-items:center;justify-content:center">
          <span style="transform:rotate(-45deg);width:${boltSize}px;height:${boltSize}px;display:flex">${glyphSvg("fast", boltSize, FAST_COLOR)}</span>
        </span>
      </span>`),
    className: "",
    iconSize: [total, total],
    iconAnchor: [total / 2, total / 2],
  });
}

// MOVE — cercle affichant directement le glyphe du mode (voiture/vélo/marche/trottinette), jamais
// l'avatar de l'organisateur : contrairement à un profil, ce qui identifie un Move au premier coup
// d'œil sur la carte c'est le moyen de transport, pas qui l'a créé (préférence explicite du
// fondateur après test — la photo derrière l'icône n'apportait rien et brouillait la lecture).
function moveIcon(visualSize: number, tapPadding = 0, mode = "ride", seed = "", selected = false) {
  const total = visualSize + tapPadding * 2;
  const borderWidth = selected ? 2.5 : 1.5;
  return L.divIcon({
    html: markerShell(total, seed || "move", `
      <span style="position:absolute;top:50%;left:50%;width:${visualSize}px;height:${visualSize}px;transform:translate(-50%,-50%);display:block;filter:drop-shadow(var(--oltodo-lift, 0 3px 10px rgba(0,0,0,0.25)))">
        <span style="position:absolute;inset:0;border-radius:50%;border:${borderWidth}px solid ${selected ? "#0a0a0a" : "#ECECEC"};box-sizing:border-box;background:white;display:flex;align-items:center;justify-content:center">${glyphSvg("move_" + mode, Math.round(visualSize * 0.46))}</span>
      </span>`),
    className: "",
    iconSize: [total, total],
    iconAnchor: [total / 2, total / 2],
  });
}

function myLocationIcon() {
  return L.divIcon({
    html: `
      <div style="position:relative;width:24px;height:24px;">
        <div style="position:absolute;inset:0;border-radius:50%;background:#0a0a0a;animation:oltodo-pulse 1.8s ease-out infinite;"></div>
        <div style="position:absolute;inset:2px;border-radius:50%;background:#0a0a0a;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);"></div>
      </div>
    `,
    className: "",
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

// Badge d'amas "moderne" pour les membres : au lieu d'un simple chiffre abstrait, on montre les
// avatars empilés des personnes du groupe — on voit tout de suite qu'il s'agit de vrais profils
// à découvrir, pas juste un nombre sur la carte.
// Photo effective d'un point, tous kinds confondus — même correspondance champ↔kind que
// markerIcon (member: avatar_url, live: url, tout le reste: cover_url), pour que la miniature
// dans un amas montre exactement la même image que le marqueur réel une fois dégroupé.
const photoOf = (point: MapPoint): string | null | undefined =>
  point.kind === "member" ? point.data.avatar_url : point.kind === "live" ? point.data.url : point.data.cover_url;

// Miniature d'un point pour un amas — RÉUTILISE la silhouette réelle du point (cercle photo,
// losange publication, rectangle Live) à petite échelle, plutôt qu'un chiffre ou un pictogramme
// nu : l'amas doit donner un vrai aperçu de ce qu'il contient, pas juste un décompte abstrait.
// C'est la même intention que Snap Map (montrer que "ça vit" avant même de zoomer) mais avec le
// vocabulaire de formes propre à OLTODO, jamais ses bulles/couleurs.
function miniShapeHtml(point: MapPoint, size: number): string {
  const url = photoOf(point);
  const fallback = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:white">${glyphSvg(point.kind, Math.round(size * 0.42))}</div>`;
  const isVideo = point.kind === "live";
  const inner = url
    ? (isVideo
        ? `<video src="${url}" muted preload="metadata" style="width:100%;height:100%;object-fit:cover;display:block"></video>`
        : `<img src="${url}" style="width:100%;height:100%;object-fit:cover;display:block" />`)
    : fallback;
  const border = "border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.25);background:white;overflow:hidden;";
  if (point.kind === "publication") {
    // Losange miniature : même construction que LosangeThumb/losangeIcon (cadre tourné à 45°,
    // contenu contre-tourné), réduite à l'échelle d'un amas.
    const diag = Math.round(size * 1.28);
    return `<div style="width:${diag}px;height:${diag}px;display:flex;align-items:center;justify-content:center;">
      <div style="width:${size}px;height:${size}px;border-radius:6px;${border}transform:rotate(45deg);position:relative;">
        <div style="position:absolute;inset:0;transform:rotate(-45deg);">${inner}</div>
      </div>
    </div>`;
  }
  if (point.kind === "live") {
    const w = Math.round(size * LIVE_RATIO), h = size;
    return `<div style="width:${w}px;height:${h}px;border-radius:6px;${border}">${inner}</div>`;
  }
  if (point.kind === "venue") {
    return `<div style="width:${size}px;height:${size}px;border-radius:${Math.max(5, Math.round(size * 0.24))}px;${border}">${inner}</div>`;
  }
  return `<div style="width:${size}px;height:${size}px;border-radius:50%;${border}">${inner}</div>`;
}

// Largeur occupée par une miniature donnée (le rectangle Live est plus large qu'un cercle/losange
// à taille égale) — nécessaire pour empiler les formes sans les faire se chevaucher n'importe
// comment quand un amas mixte contient des Lives à côté de cercles.
const miniShapeWidth = (point: MapPoint, size: number) => point.kind === "live" ? Math.round(size * LIVE_RATIO) : size;

// UN SEUL constructeur d'icône d'amas, quel que soit son contenu (mono-type ou mixte) — remplace
// l'ancien trio avatarStackClusterIcon/typedClusterIcon/mixedClusterIcon, qui rendait soit des
// avatars empilés (membres seulement), soit un texte "N lieux" dans une pastille noire, soit un
// bloc blanc listant plusieurs lignes "N type" pour un amas mixte : trois rendus différents, et
// aucun ne montrait jamais le VRAI contenu d'un amas non-membre. Ici, un échantillon réellement
// varié (jusqu'à 4 points, en piochant large — voir plus bas) rend sa propre miniature (cercle
// photo / losange / rectangle Live, selon son kind), avec un badge "+N" pour le reste — la carte
// dézoomée ressemble alors à un vrai aperçu du contenu, pas à un décompte abstrait.
function stackClusterIcon(points: MapPoint[], totalCount: number) {
  const SIZE = 30, STEP = 18;
  // Échantillon diversifié : un point par kind présent, dans l'ordre où les kinds apparaissent,
  // puis on complète avec d'autres points du kind le plus fréquent si moins de 4 kinds distincts
  // sont représentés — un amas de 40 profils doit quand même montrer plusieurs vraies photos,
  // pas un seul visage répété.
  const byKind = new Map<string, MapPoint[]>();
  points.forEach(p => { const arr = byKind.get(p.kind) || []; arr.push(p); byKind.set(p.kind, arr); });
  const sample: MapPoint[] = [];
  Array.from(byKind.values()).forEach(arr => { if (sample.length < 4) sample.push(arr[0]); });
  if (sample.length < 4) {
    const [biggestKind] = Array.from(byKind.entries()).sort((a, b) => b[1].length - a[1].length)[0] || [];
    (byKind.get(biggestKind) || []).forEach(p => {
      if (sample.length >= 4) return;
      if (!sample.includes(p)) sample.push(p);
    });
  }
  const extra = totalCount - sample.length;

  let x = 0;
  const shapes = sample.map(point => {
    const w = miniShapeWidth(point, SIZE);
    const html = `<div style="position:absolute;left:${x}px;top:0;">${miniShapeHtml(point, SIZE)}</div>`;
    x += STEP;
    return { html, w };
  });
  const contentWidth = Math.max(...shapes.map((s, i) => i * STEP + s.w), SIZE);
  const extraBadge = extra > 0
    ? `<div style="position:absolute;left:${x}px;top:0;width:${SIZE}px;height:${SIZE}px;border-radius:50%;background:#0a0a0a;color:white;font-size:10.5px;font-weight:700;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.25);">+${extra}</div>`
    : "";
  const width = extra > 0 ? x + SIZE : contentWidth;
  return L.divIcon({
    html: `<div style="position:relative;height:${SIZE}px;width:${width}px;">${shapes.map(s => s.html).join("")}${extraBadge}</div>`,
    className: "",
    iconSize: [width, SIZE],
    iconAnchor: [width / 2, SIZE / 2],
  });
}

type MapPoint = { kind: string; data: any };

// UNE seule couche d'amas pour TOUS les types à la fois — c'est le cœur du changement. Avant,
// chaque type avait son propre markerClusterGroup : cinq couches indépendantes empilées, chacune
// incapable de voir les autres. Un amas ne pouvait donc dire que "12" (des membres) ou "5" (des
// lieux), jamais "23 profils, 8 lieux, 5 communautés" pour une même zone, et deux amas de types
// différents se superposaient au même endroit. Ici les marqueurs sont regroupés par PROXIMITÉ
// réelle, tous types confondus, et l'icône de l'amas décrit son contenu (voir mixedClusterIcon).
// Le clic sur l'amas zoome, et au niveau de zoom max il "spiderfie" (les éparpille en éventail).
// Le clic sur un marqueur ne fait pas apparaître de popup Leaflet (trop peu fiable au tactile) :
// il ouvre directement la fiche de l'élément dans le tiroir du bas.
function ClusterLayer({ points, getIcon, onMarkerClick, clusterRadius, spiderfyDistanceMultiplier, deps = [] }: {
  points: MapPoint[];
  getIcon: (point: MapPoint) => L.DivIcon;
  onMarkerClick?: (point: MapPoint) => void;
  clusterRadius: number;
  spiderfyDistanceMultiplier?: number;
  deps?: any[];
}) {
  const map = useMap();
  useEffect(() => {
    const group = (L as any).markerClusterGroup({
      showCoverageOnHover: false,
      // spiderfyOnMaxZoom compare le zoom courant au maxZoom RÉEL de la carte (19, cf. TileLayer
      // maxZoom plus bas) — jamais à un seuil arbitraire. On avait auparavant un
      // disableClusteringAtZoom={17} qui semblait résoudre "le spiderfy ne se déclenche jamais",
      // mais faisait en réalité disparaître le regroupement AVANT que zoomToBoundsOnClick n'ait
      // fini de zoomer : un amas de points à coordonnées quasi identiques se dissolvait en pins
      // bruts superposés dès 17, sans jamais atteindre le vrai maxZoom où le spiderfy prend le
      // relais. Sans ce seuil, zoomToBoundsOnClick zoome tant que ça aide à séparer les points, et
      // bascule naturellement sur spiderfy() dès que zoomer davantage n'apporterait plus rien
      // (points à la même adresse, même bâtiment...) — exactement le comportement demandé :
      // déploiement en éventail au clic, sans jamais attendre une action supplémentaire.
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true,
      maxClusterRadius: clusterRadius,
      // Distance augmentée par rapport à l'ancien design (1.7) : les nouvelles silhouettes
      // (cercles à photo, losanges, rectangles Live) sont visuellement plus grandes et plus
      // variées qu'un simple avatar rond — il leur faut plus d'espace pour rester lisibles une
      // fois déployées en éventail, sous peine de se chevaucher à nouveau après spiderfy.
      spiderfyDistanceMultiplier: spiderfyDistanceMultiplier || 2.2,
      iconCreateFunction: (cluster: any) => {
        const children = cluster.getAllChildMarkers();
        const childPoints: MapPoint[] = children.map((m: any) => m.__oltodoPoint);
        return stackClusterIcon(childPoints, childPoints.length);
      },
    });
    points.forEach(point => {
      const marker = L.marker([Number(point.data.latitude), Number(point.data.longitude)], { icon: getIcon(point) });
      // Le point complet (pas seulement son kind) est nécessaire à stackClusterIcon pour rendre
      // la vraie miniature de chaque élément (photo, forme) une fois regroupé en amas.
      (marker as any).__oltodoPoint = point;
      if (onMarkerClick) marker.on("click", () => onMarkerClick(point));
      group.addLayer(marker);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, map, clusterRadius, ...deps]);
  return null;
}

// Ce qu'une card FAST montre AVANT de rejoindre : jamais l'adresse exacte (voir exact_address,
// révélée uniquement après "J'arrive" — CASE WHEN de routes/fasts.ts), mais assez pour situer :
// le nom du lieu s'il y en a un, complété par la ville (ou quartier + ville) calculée une fois à
// la création. Sans lieu, "Ma position actuelle" seul ne disait rien à qui doit s'y rendre — la
// zone approximative remplace ce texte générique.
const fastLocationText = (d: any): string =>
  d.venue_id ? (d.location_label ? `${d.place_label} · ${d.location_label}` : d.place_label) : (d.location_label || d.place_label);

const distanceLabel = (item: any, center: { lat: number; lng: number }) => {
  if (!item.latitude || !item.longitude) return null;
  const R = 6371;
  const dLat = (Number(item.latitude) - center.lat) * Math.PI / 180;
  const dLng = (Number(item.longitude) - center.lng) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(center.lat * Math.PI / 180) * Math.cos(Number(item.latitude) * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  const km = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
};

export default function Explore() {
  const { token, user, lang } = useApp();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // Filtres COMBINABLES : un ensemble de catégories actives, pas une catégorie unique. "Tout"
  // = toutes actives. Tout ce qui suit lit cet ensemble (showMembers, showLives…), plus jamais
  // une égalité de chaîne.
  const [selected, setSelected] = useState<Set<string>>(new Set(ALL_CATEGORY_KEYS));
  const [passionFilter, setPassionFilter] = useState<string | null>(null);
  const [powerFilter, setPowerFilter] = useState<string | null>(null);
  // Jauge de périmètre (façon Tinder) — révélée par le 3e bouton flottant, au-dessus de
  // "recentrer"/"ajouter un lieu". S'applique à tout ce qui est géolocalisé sur la carte.
  const [searchRadiusKm, setSearchRadiusKm] = useState(50);
  const [showRadiusGauge, setShowRadiusGauge] = useState(false);
  // Sous-filtre "secteur d'activité" pour les lieux, actif uniquement quand le chip "Lieux" l'est —
  // valeur = une famille de VENUE_TYPE_FAMILIES (voir CreateVenueModal.tsx), envoyée au backend
  // comme liste de types exacts séparés par des virgules.
  const [venueFamilyFilter, setVenueFamilyFilter] = useState<string | null>(null);

  // Emplacement effectif : GPS temps réel si autorisé, sinon la ville du profil, sinon Paris.
  const profileCenter = user?.latitude && user?.longitude ? { lat: Number(user.latitude), lng: Number(user.longitude) } : null;
  const [center, setCenter] = useState(profileCenter || DEFAULT_CENTER);
  const [locationSource, setLocationSource] = useState<"gps" | "profile" | "default">(profileCenter ? "profile" : "default");
  // viewCenter pilote la recherche (peut être une autre ville, choisie en déplaçant la carte) ;
  // center reste "chez moi", utilisé par le bouton recentrer — les deux ne doivent jamais se confondre.
  const [viewCenter, setViewCenter] = useState(profileCenter || DEFAULT_CENTER);
  const [hasExplicitSearch, setHasExplicitSearch] = useState(false);
  const [pendingCenter, setPendingCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [drilldownCategory, setDrilldownCategory] = useState<string | null>(null);
  // Fiche d'UN seul élément : ce qu'un tap sur la carte affiche désormais en premier, au lieu de
  // déverser toute la liste de sa catégorie. La liste reste accessible juste en dessous, via
  // "Voir les autres …" — elle n'est plus imposée, elle est proposée.
  const [detailItem, setDetailItem] = useState<MapPoint | null>(null);
  const [liveLightbox, setLiveLightbox] = useState<any | null>(null);
  // Palier de zoom courant : la SEULE chose que le JS calcule pour l'effet de profondeur. Il
  // devient une classe sur la carte, et le CSS s'occupe de tout le reste (voir <style>).
  const [zoomBucket, setZoomBucket] = useState<ZoomBucket>("far");
  const onZoomBucket = React.useCallback((b: ZoomBucket) => setZoomBucket(prev => (prev === b ? prev : b)), []);
  const mapRef = useRef<L.Map | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dragStartY = useRef<number | null>(null);
  const dragMoved = useRef(false);
  const touchHandledRef = useRef(false);

  const [members, setMembers] = useState<any[]>([]);
  const [communities, setCommunities] = useState<any[]>([]);
  const [rituals, setRituals] = useState<any[]>([]);
  const [publications, setPublications] = useState<any[]>([]);
  const [venues, setVenues] = useState<any[]>([]);
  // Lieux-prestataires pour la catégorie Power sélectionnée (agenda réellement ouvert) — couche
  // additive et distincte du chip "Lieux" générique ci-dessus, qui reste 100% inchangé.
  const [providerVenues, setProviderVenues] = useState<any[]>([]);
  const [lives, setLives] = useState<any[]>([]);
  const [fasts, setFasts] = useState<any[]>([]);
  const [moves, setMoves] = useState<any[]>([]);
  const [moveDetailId, setMoveDetailId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateVenue, setShowCreateVenue] = useState(false);
  const [showFastCreate, setShowFastCreate] = useState(false);
  // Compteurs/état "j'arrive" tenus ici plutôt que dans renderDetailCard (fonction, pas un
  // composant — impossible d'y appeler useState en toute sécurité si elle n'est invoquée que
  // conditionnellement, ce qui est le cas).
  const [arrivedFastIds, setArrivedFastIds] = useState<Set<string>>(new Set());
  const [fastParticipantCounts, setFastParticipantCounts] = useState<Record<string, number>>({});


  // On repart toujours du menu des catégories en rouvrant le tiroir, plutôt que de rester
  // coincé dans une sous-liste — ou sur une fiche — ouverte précédemment.
  useEffect(() => { if (!sheetExpanded) { setDrilldownCategory(null); setDetailItem(null); } }, [sheetExpanded]);

  // Fait défiler la carte sélectionnée dans le tiroir une fois qu'elle est réellement montée à
  // l'écran (après que le tiroir se soit déplié / que la bonne catégorie soit affichée) — un
  // appel direct dans le clic lui-même arrivait trop tôt, avant que la liste ne soit rendue.
  useEffect(() => {
    if (!selectedId) return;
    const el = cardRefs.current[selectedId];
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "center" });
  }, [selectedId, sheetExpanded, drilldownCategory]);

  // Demande la position GPS réelle (comme Tinder/Snapchat) : si l'utilisateur l'autorise,
  // elle prend le dessus sur la ville du profil, sans jamais bloquer l'affichage de la carte.
  // Le survol de la carte se fait directement sur l'instance Leaflet (mapRef), jamais via un
  // état React intermédiaire : c'est cette indirection qui rendait le bouton "recentrer"
  // silencieusement inopérant (voir recenter() plus bas pour l'explication complète).
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => {
        const gps = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(gps);
        setViewCenter(gps);
        setLocationSource("gps");
        mapRef.current?.flyTo([gps.lat, gps.lng], 14, { duration: 0.6 });
      },
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
  }, []);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    // [[TEMP-VERIF-MOCK — À SUPPRIMER]]
    if (new URLSearchParams(window.location.search).has("mock")) {
      const c = { lat: 48.8566, lng: 2.3522 };
      const jitter = (i: number) => ({ latitude: c.lat + (i % 5) * 0.004 - 0.008, longitude: c.lng + Math.floor(i / 5) * 0.005 - 0.008 });
      const img = (t: string) => `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="120" height="80"><rect width="120" height="80" fill="#333"/><text x="60" y="46" font-size="16" fill="#fff" text-anchor="middle">${t}</text></svg>`)}`;
      setMembers([
        { id: "m1", name: "Alice Martin", score: 812, city: "Paris", avatar_url: img("A"), passions: ["Yoga"], powers: ["Coach"], ...jitter(0) },
        { id: "m2", name: "Bruno Diaz", score: 430, city: "Paris", is_friend: true, ...jitter(1) },
      ]);
      setVenues([{ id: "v1", name: "Le Studio", type: "Studio", cover_url: img("V"), ...jitter(2) }]);
      setRituals([{ id: "r1", title: "Course du matin", frequency: "Hebdo", members_count: 12, ...jitter(3) }]);
      setCommunities([{ id: "c1", name: "Les Grimpeurs", members_count: 40, cover_url: img("C"), ...jitter(4) }]);
      setPublications([
        { id: "p1", title: "Atelier céramique", type: "passion", category: "Événement", author_name: "Alice", price: 0, is_free: true, cover_url: img("P"), ...jitter(5) },
        { id: "p2", title: "Coaching vocal", type: "power", category: "Power", author_name: "Bruno", price: 35, ...jitter(6) },
        { id: "p3", title: "Rando nocturne", type: "passion", category: "Passion", author_name: "Alice", price: 0, is_free: true, ...jitter(7) },
      ]);
      setLives([{ id: "l1", author_name: "Alice", url: "", created_at: new Date().toISOString(), views_count: 8, likes_count: 3, ...jitter(8) }]);
      setLoading(false);
      return;
    }
    // Une recherche explicite ("Rechercher dans cette zone") force la géolocalisation même
    // sans position connue, pour pouvoir parcourir une ville où l'on ne se trouve pas soi-même —
    // un rayon plus large que le "près de moi" par défaut, pour couvrir toute l'agglomération visée.
    const useGeo = locationSource !== "default" || hasExplicitSearch;
    const geoParams = new URLSearchParams();
    if (useGeo) {
      geoParams.set("lat", String(viewCenter.lat));
      geoParams.set("lng", String(viewCenter.lng));
      // La jauge pilote le rayon partout, sauf une "recherche dans cette zone" explicite qui veut
      // toujours couvrir toute l'agglomération visée même si la jauge est réglée plus bas.
      geoParams.set("radius", String(hasExplicitSearch ? Math.max(searchRadiusKm, 80) : searchRadiusKm));
    }
    const params = new URLSearchParams(geoParams);
    if (passionFilter) params.set("passion", passionFilter);
    if (powerFilter) params.set("power", powerFilter);
    const venueFamilyTypes = venueFamilyFilter ? VENUE_TYPE_FAMILIES.find(f => f.family === venueFamilyFilter)?.types || [] : [];
    const venueParams = new URLSearchParams(geoParams);
    venueParams.set("include_followed", "1");
    if (venueFamilyTypes.length) venueParams.set("type", venueFamilyTypes.join(","));
    Promise.all([
      api.get(`/users/discover${params.toString() ? `?${params}` : ""}`).catch(() => ({ users: [] })),
      api.get("/communities/nearby").catch(() => ({ communities: [] })),
      api.get(`/rituals/discover${geoParams.toString() ? `?${geoParams}` : ""}`).catch(() => ({ rituals: [] })),
      api.get("/publications/explore").catch(() => ({ publications: [] })),
      // include_followed=1 : sans ça, un lieu qu'on suit déjà disparaît de la carte (l'endpoint
      // sert aussi de suggestions "lieux à suivre" sur Home/Search, qui eux veulent l'exclure) —
      // ici c'est l'inverse, tout l'intérêt est de voir ce qu'il s'y passe une fois abonné.
      api.get(`/venues/nearby?${venueParams}`).catch(() => ({ venues: [] })),
      // Lieux-prestataires pour CETTE catégorie précise (agenda réellement ouvert) — n'appelle le
      // serveur que si un power précis est sélectionné.
      powerFilter
        ? api.get(`/venues/nearby?${new URLSearchParams({ ...Object.fromEntries(geoParams), power: powerFilter, include_followed: "1" })}`).catch(() => ({ venues: [] }))
        : Promise.resolve({ venues: [] }),
      // Les Lives sont géolocalisés par l'activité à laquelle ils sont liés (publication, rituel
      // ou communauté) — le backend résout déjà cette jointure, on consomme simplement
      // latitude/longitude comme pour n'importe quel autre contenu.
      api.get(`/users/lives/nearby${geoParams.toString() ? `?${geoParams}` : ""}`).catch(() => ({ lives: [] })),
      api.get(`/fasts/nearby${geoParams.toString() ? `?${geoParams}` : ""}`).catch(() => ({ fasts: [] })),
      api.get(`/publications/moves/nearby${geoParams.toString() ? `?${geoParams}` : ""}`).catch(() => ({ moves: [] })),
    ]).then(([u, c, r, p, v, pv, l, q, mv]) => {
      setMembers(u.users || []);
      setCommunities(c.communities || []);
      setRituals(r.rituals || []);
      setPublications(p.publications || []);
      setVenues(v.venues || []);
      setProviderVenues(pv.venues || []);
      setLives(l.lives || []);
      setFasts(q.fasts || []);
      setMoves(mv.moves || []);
    }).finally(() => setLoading(false));
  }, [token, passionFilter, powerFilter, venueFamilyFilter, searchRadiusKm, locationSource, hasExplicitSearch, viewCenter.lat, viewCenter.lng]);

  // Bascule la recherche sur le centre actuel de la carte (potentiellement une tout autre ville) —
  // seuil de 5km pour ignorer les micro-déplacements et ne proposer le bouton que pour un vrai
  // changement de zone.
  const SEARCH_AREA_THRESHOLD_KM = 5;
  const searchThisArea = () => {
    if (!pendingCenter) return;
    setViewCenter(pendingCenter);
    setHasExplicitSearch(true);
    setPendingCenter(null);
  };
  const showSearchAreaButton = !!pendingCenter && haversineKm(pendingCenter, viewCenter) > SEARCH_AREA_THRESHOLD_KM;

  const followVenue = (id: string) => {
    api.post(`/venues/${id}/follow`, {}).then(() => setVenues(prev => prev.filter(v => v.id !== id))).catch(() => {});
  };

  const membersWithGeo = members.filter(m => m.latitude && m.longitude);
  const nearbyPassions = useMemo(() => rankBySkillFrequency(membersWithGeo, "passions"), [membersWithGeo]);
  // "bookable_powers" et non "powers" : une puce Power n'a de sens que si cliquer dessus renvoie
  // au moins un profil — le filtre ?power= (voir /users/discover côté backend) n'accepte que les
  // powers avec un agenda ouvert, donc la liste de puces doit refléter exactement ce même critère.
  const nearbyPowers = useMemo(() => rankBySkillFrequency(membersWithGeo, "bookable_powers"), [membersWithGeo]);
  const ritualsWithGeo = rituals.filter(r => r.latitude && r.longitude);
  const pubsWithGeo = publications.filter(p => p.latitude && p.longitude);
  const communitiesWithGeo = communities.filter(c => c.latitude && c.longitude);
  // Un lieu-prestataire pour la catégorie sélectionnée est fusionné dans la même liste que les
  // lieux "génériques" (même kind "venue", même marqueur carré) — seule différence visuelle :
  // is_power_provider déclenche un anneau or plutôt que blanc, sans ajouter de nouvelle forme ni
  // de nouvelle couleur au système existant. Un lieu déjà présent dans `venues` (ex. suivi) qui
  // est AUSSI prestataire de ce power garde une seule entrée, jamais de doublon sur la carte.
  const providerVenueIds = new Set(providerVenues.map(v => v.id));
  const venuesWithGeo = [
    ...venues.filter(v => v.latitude && v.longitude).map(v => ({ ...v, is_power_provider: providerVenueIds.has(v.id) })),
    ...providerVenues.filter(v => v.latitude && v.longitude && !venues.some(existing => existing.id === v.id)).map(v => ({ ...v, is_power_provider: true })),
  ];
  const livesWithGeo = lives.filter(l => l.latitude && l.longitude);
  const fastsWithGeo = fasts.filter(q => q.latitude && q.longitude);
  const movesWithGeo = moves.filter(m => m.latitude && m.longitude);
  // Comme nearbyPassions/nearbyPowers : on ne propose que les modes réellement présents à
  // proximité (un seul Move "Ride" alentour ne doit pas afficher les puces Bike/Walk/Scoot vides).
  const MOVE_MODE_ORDER: MoveMode[] = ["ride", "bike", "walk", "scoot"];
  const nearbyMoveModes = useMemo(() => {
    const present = new Set(movesWithGeo.map(m => m.move_mode));
    return MOVE_MODE_ORDER.filter(m => present.has(m));
  }, [movesWithGeo]);
  const eventsWithGeo = pubsWithGeo.filter(p => p.type === "passion" || p.type === "power");

  // Principe d'affichage d'Explorer : toujours la proximité d'abord, puis les amis (qui
  // remontent devant des inconnus à distance comparable), puis on s'éloigne progressivement —
  // jamais un tri par type qui cacherait des profils/activités proches derrière une catégorie.
  const distanceOf = (item: any) => haversineKm(viewCenter, { lat: Number(item.latitude), lng: Number(item.longitude) });
  const DISTANCE_BUCKET_KM = 5;
  const sortByProximityThenFriends = <T extends { data: any }>(items: T[]): T[] => {
    return [...items].sort((a, b) => {
      const da = distanceOf(a.data), db = distanceOf(b.data);
      const bucketA = Math.round(da / DISTANCE_BUCKET_KM), bucketB = Math.round(db / DISTANCE_BUCKET_KM);
      if (bucketA !== bucketB) return da - db;
      const fa = a.data.is_friend ? 0 : 1;
      const fb = b.data.is_friend ? 0 : 1;
      if (fa !== fb) return fa - fb;
      return da - db;
    });
  };

  const showMembers = selected.has("members");
  const showRituals = selected.has("rituals");
  const showPassions = selected.has("passions");
  const showPowers = selected.has("powers");
  const showCommunities = selected.has("communities");
  const showVenues = selected.has("venues");
  const showLives = selected.has("lives");
  const showFasts = selected.has("fasts");
  const showMoves = selected.has("moves");
  // Sous-filtre par mode (Ride/Bike/Walk/Scoot), même motif que le sous-filtre passions/powers
  // sous "Membres" — un Set vide = tous les modes affichés (jamais "aucun résultat" par défaut).
  const [moveModeFilter, setMoveModeFilter] = useState<Set<MoveMode>>(new Set());
  const movesFiltered = moveModeFilter.size === 0 ? movesWithGeo : movesWithGeo.filter(m => moveModeFilter.has(m.move_mode));
  const toggleMoveMode = (mode: MoveMode) => setMoveModeFilter(prev => {
    const next = new Set(prev);
    if (next.has(mode)) next.delete(mode); else next.add(mode);
    return next;
  });
  const shownPubsWithGeo = pubsWithGeo.filter(p => (p.type === "passion" && showPassions) || (p.type === "power" && showPowers));
  const isAll = selected.size === ALL_CATEGORY_KEYS.length;
  // Un seul type coché : inutile de proposer un menu de catégories à une seule entrée, on affiche
  // la liste directement (comportement identique à l'ancien filtre unique).
  const isSingleSelection = selected.size === 1;
  const toggleCategory = (key: string) => {
    setSelected(prev => {
      // Depuis "Tout", aucune puce n'est allumée : le premier appui doit donc ALLUMER celle
      // qu'on touche (= ne voir que ça), pas éteindre discrètement la seule catégorie demandée.
      // Ensuite seulement, les appuis suivants ajoutent/retirent des types à la sélection.
      if (prev.size === ALL_CATEGORY_KEYS.length) return new Set([key]);
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setSelectedId(null);
    setDetailItem(null);
    setDrilldownCategory(null);
  };
  const selectAllCategories = () => {
    setSelected(new Set(ALL_CATEGORY_KEYS));
    setSelectedId(null);
    setDetailItem(null);
    setDrilldownCategory(null);
  };

  // Bouton "recentrer" — corrigé DÉFINITIVEMENT. L'ancienne version passait par un état React
  // (setFocus) observé par un effet qui ne se redéclenchait que si lat/lng changeaient de
  // valeur ; en pratique center ne change quasiment jamais après le premier chargement, donc
  // cliquer une seconde fois ne faisait plus rien puisque l'effet ne voyait aucun changement.
  // Ici on pilote directement l'instance Leaflet (mapRef) : chaque clic déclenche un vrai
  // déplacement, peu importe l'état précédent.
  const recenter = () => {
    if (mapRef.current) mapRef.current.flyTo([center.lat, center.lng], 14, { duration: 0.6 });
    // Revenir "chez moi" doit aussi ramener les résultats à mes environs, pas laisser la carte
    // visuellement recentrée tout en gardant les pins d'une ville lointaine recherchée avant.
    setViewCenter(center);
    setHasExplicitSearch(false);
    setPendingCenter(null);
  };

  // Tiroir de découverte : TOUS les types y apparaissent désormais, membres et lieux compris —
  // avant, seuls rituels/publications avaient une carte, et il fallait deviner/viser un pin sur
  // la carte pour découvrir un profil. Ce n'était pas fiable au tactile : c'est maintenant la
  // liste qui fait le travail, la carte ne sert plus qu'à visualiser où.
  const drawerItems = useMemo(() => {
    const all = [
      ...(showMembers ? membersWithGeo.map(m => ({ kind: "member" as const, data: m })) : []),
      ...(showVenues ? venuesWithGeo.map(v => ({ kind: "venue" as const, data: v })) : []),
      ...(showCommunities ? communitiesWithGeo.map(c => ({ kind: "community" as const, data: c })) : []),
      ...(showRituals ? ritualsWithGeo.map(r => ({ kind: "ritual" as const, data: r })) : []),
      ...shownPubsWithGeo.map(p => ({ kind: "publication" as const, data: p })),
      ...(showLives ? livesWithGeo.map(l => ({ kind: "live" as const, data: l })) : []),
      ...(showFasts ? fastsWithGeo.map(q => ({ kind: "fast" as const, data: q })) : []),
      ...(showMoves ? movesFiltered.map(mv => ({ kind: "move" as const, data: mv })) : []),
    ];
    return sortByProximityThenFriends(all);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, membersWithGeo, venuesWithGeo, communitiesWithGeo, ritualsWithGeo, shownPubsWithGeo, livesWithGeo, fastsWithGeo, movesFiltered, viewCenter.lat, viewCenter.lng]);

  // Les marqueurs de la carte : exactement les mêmes éléments que le tiroir, dans une seule
  // liste — c'est elle qui alimente l'unique couche d'amas, tous types confondus.
  const mapPoints: MapPoint[] = drawerItems;

  // Vue "Tout" du tiroir replié — carrousel mélangeant vraiment tous les types (profils, lieux,
  // communautés, rituels, événements), pas seulement les personnes connectées : on veut montrer
  // le maximum d'infos disponibles, pas une sélection restreinte. Triée par proximité puis amis.
  const peekMixed = useMemo(() => {
    if (!isAll) return drawerItems;
    const all = [
      ...membersWithGeo.map(m => ({ kind: "member" as const, data: m })),
      ...venuesWithGeo.map(v => ({ kind: "venue" as const, data: v })),
      ...communitiesWithGeo.map(c => ({ kind: "community" as const, data: c })),
      ...ritualsWithGeo.map(r => ({ kind: "ritual" as const, data: r })),
      ...eventsWithGeo.map(p => ({ kind: "publication" as const, data: p })),
      ...livesWithGeo.map(l => ({ kind: "live" as const, data: l })),
      ...fastsWithGeo.map(q => ({ kind: "fast" as const, data: q })),
      ...movesWithGeo.map(mv => ({ kind: "move" as const, data: mv })),
    ];
    return sortByProximityThenFriends(all);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAll, drawerItems, membersWithGeo, venuesWithGeo, communitiesWithGeo, ritualsWithGeo, eventsWithGeo, livesWithGeo, fastsWithGeo, movesWithGeo, viewCenter.lat, viewCenter.lng]);

  // Menu des catégories du tiroir déplié (vue "Tout") : on choisit d'abord QUOI regarder, la
  // liste elle-même ne s'affiche qu'après avoir choisi une catégorie — chaque liste reste triée
  // par proximité puis amis.
  // Un groupe par catégorie de contenu, dans le même vocabulaire que les puces du haut et que le
  // bouton "Voir les autres …" d'une fiche : les rituels ne sont plus noyés avec les publications
  // dans un fourre-tout "Événements", sinon "Voir les autres Rituels" ouvrirait autre chose que
  // des rituels. Un groupe n'existe que si sa catégorie est cochée.
  const categoryGroups = useMemo(() => [
    { key: "profiles", label: t(lang,"explore_group_profiles")||"Profils", show: showMembers, items: sortByProximityThenFriends(membersWithGeo.map(m => ({ kind: "member" as const, data: m }))) },
    { key: "venues", label: t(lang,"explore_tab_venues")||"Lieux", show: showVenues, items: sortByProximityThenFriends(venuesWithGeo.map(v => ({ kind: "venue" as const, data: v }))) },
    { key: "rituals", label: t(lang,"explore_tab_rituals")||"Rituels", show: showRituals, items: sortByProximityThenFriends(ritualsWithGeo.map(r => ({ kind: "ritual" as const, data: r }))) },
    { key: "communities", label: t(lang,"explore_tab_communities")||"Communautés", show: showCommunities, items: sortByProximityThenFriends(communitiesWithGeo.map(c => ({ kind: "community" as const, data: c }))) },
    { key: "publications", label: t(lang,"explore_kind_publication")||"Publications", show: showPassions || showPowers, items: sortByProximityThenFriends(shownPubsWithGeo.map(p => ({ kind: "publication" as const, data: p }))) },
    { key: "lives", label: t(lang,"explore_tab_lives")||"Lives", show: showLives, items: sortByProximityThenFriends(livesWithGeo.map(l => ({ kind: "live" as const, data: l }))) },
    { key: "fasts", label: t(lang,"explore_tab_fasts")||"Fasts", show: showFasts, items: sortByProximityThenFriends(fastsWithGeo.map(q => ({ kind: "fast" as const, data: q }))) },
    { key: "moves", label: t(lang,"explore_tab_moves")||"Move", show: showMoves, items: sortByProximityThenFriends(movesFiltered.map(mv => ({ kind: "move" as const, data: mv }))) },
  ].filter(g => g.show),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  [lang, selected, membersWithGeo, venuesWithGeo, ritualsWithGeo, communitiesWithGeo, shownPubsWithGeo, livesWithGeo, fastsWithGeo, movesFiltered, viewCenter.lat, viewCenter.lng]);

  // Correspondance type de contenu → groupe de liste, utilisée par le bouton "Voir les autres …"
  // d'une fiche : depuis un rituel on ouvre la liste des rituels, depuis un Live celle des Lives.
  const KIND_TO_DRILLDOWN: Record<string, string> = { member: "profiles", venue: "venues", community: "communities", ritual: "rituals", publication: "publications", live: "lives", fast: "fasts", move: "moves" };
  const KIND_PLURAL: Record<string, string> = {
    member: t(lang,"explore_group_profiles")||"Profils", venue: t(lang,"explore_tab_venues")||"Lieux",
    community: t(lang,"explore_tab_communities")||"Communautés", ritual: t(lang,"explore_tab_rituals")||"Rituels",
    publication: t(lang,"explore_kind_publication")||"Publications", live: t(lang,"explore_tab_lives")||"Lives", fast: t(lang,"explore_tab_fasts")||"Fasts",
    move: t(lang,"explore_tab_moves")||"Move",
  };

  // Un tap sur un marqueur (ou sur une carte/ligne du tiroir) ouvre IMMÉDIATEMENT la fiche de CET
  // élément-là, et rien d'autre : photo, nom, l'essentiel. Avant, le moindre tap dépliait toute
  // la liste de la catégorie — on demandait à voir un lieu, on recevait quarante résultats.
  // La liste n'a pas disparu pour autant : elle est à un bouton de là ("Voir les autres …").
  const openDetail = (point: MapPoint) => {
    const { data } = point;
    setDetailItem(point);
    setSelectedId(data.id);
    setDrilldownCategory(null);
    setSheetExpanded(true);
    const lat = data.latitude ? Number(data.latitude) : undefined;
    const lng = data.longitude ? Number(data.longitude) : undefined;
    if (lat && lng && mapRef.current) {
      // Zoomer nettement plus rapproche suffisamment la carte pour que le marqueur sélectionné
      // reste bien visible et isolé au-dessus du tiroir.
      const targetZoom = 17;
      const offsetPx = window.innerHeight * 0.64;
      flyAboveSheet(mapRef.current, [lat, lng], Math.max(mapRef.current.getZoom(), targetZoom), offsetPx);
    }
  };

  // "J'arrive" — instantané, jamais de validation par l'organisateur (voir routes/fasts.ts).
  // Optimiste : la coche et le compteur bougent tout de suite, corrigés silencieusement en cas
  // d'échec réseau plutôt que de bloquer l'utilisateur sur un état de chargement.
  const arriveAtFast = (fastId: string) => {
    if (arrivedFastIds.has(fastId)) return;
    setArrivedFastIds(prev => new Set(prev).add(fastId));
    setFastParticipantCounts(prev => ({ ...prev, [fastId]: (prev[fastId] ?? 0) + 1 }));
    api.post(`/fasts/${fastId}/arrive`, {}).then(data => {
      if (typeof data?.participants_count === "number") {
        setFastParticipantCounts(prev => ({ ...prev, [fastId]: data.participants_count }));
      }
      // La réponse vient de nous donner le droit de voir l'adresse exacte (voir CASE WHEN de
      // routes/fasts.ts) — on la pose directement sur l'élément en state, pas besoin d'un second
      // aller-retour : `d.exact_address` (dérivé de `fasts`) la reflète au prochain rendu.
      if (data?.exact_address) {
        setFasts(prev => prev.map(f => (f.id === fastId ? { ...f, exact_address: data.exact_address } : f)));
      }
    }).catch(() => {
      setArrivedFastIds(prev => { const next = new Set(prev); next.delete(fastId); return next; });
      setFastParticipantCounts(prev => ({ ...prev, [fastId]: Math.max(0, (prev[fastId] ?? 1) - 1) }));
    });
  };

  // Depuis la fiche : passer à la liste complète de la même catégorie.
  const showOthersOf = (kind: string) => {
    setDetailItem(null);
    setDrilldownCategory(KIND_TO_DRILLDOWN[kind] || null);
  };

  // Retour à l'état normal du tiroir (carrousel replié), depuis une fiche comme depuis une liste.
  const closeDetail = () => {
    setDetailItem(null);
    setSelectedId(null);
    setSheetExpanded(false);
  };

  // Geste de tirage sur la poignée du tiroir, EN PLUS du tap — le réflexe naturel de
  // remonter/descendre le doigt doit marcher aussi, pas seulement un clic précis sur la
  // poignée. Limité à la poignée elle-même (pas toute la zone de la liste) pour ne jamais
  // entrer en conflit avec le défilement des cartes.
  const onHandleTouchStart = (e: React.TouchEvent) => { dragStartY.current = e.touches[0].clientY; dragMoved.current = false; };
  const onHandleTouchMove = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    if (Math.abs(e.touches[0].clientY - dragStartY.current) > 10) dragMoved.current = true;
  };
  const onHandleTouchEnd = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.changedTouches[0].clientY - dragStartY.current;
    dragStartY.current = null;
    touchHandledRef.current = true;
    setTimeout(() => { touchHandledRef.current = false; }, 400);
    if (!dragMoved.current) { setSheetExpanded(v => !v); return; }
    if (delta < -30) setSheetExpanded(true);
    else if (delta > 30) setSheetExpanded(false);
  };
  const onHandleClick = () => {
    if (touchHandledRef.current) return;
    setSheetExpanded(v => !v);
  };


  // Carte du carrousel replié — UNE seule apparence pour tous les types, quel que soit le
  // filtre choisi en haut ("Tout" ou un type précis) : avant, changer de filtre faisait
  // retomber sur l'ancien style de carte, ce n'était pas cohérent. Personnes : avatar + prénom
  // + statut. Tout le reste : photo (ou pictogramme si pas de photo) + catégorie + titre en
  // surimpression, pour comprendre de quoi il s'agit d'un coup d'œil.
  const KIND_BADGE: Record<string, (data: any) => string> = {
    venue: v => v.type || t(lang, "publish_as_type_venue"),
    community: () => t(lang, "publish_as_type_community"),
    ritual: () => t(lang, "publish_as_type_ritual"),
    publication: p => p.category || t(lang, "stories_link_event"),
    live: () => t(lang, "expl_live"),
    fast: q => FAST_CATEGORY_LABELS[q.category as FastCategoryKey] || "FAST",
  };

  const renderPeekCard = (item: { kind: string; data: any }) => {
    const setRef = (el: HTMLDivElement | null) => { cardRefs.current[item.data.id] = el; };
    const isSel = selectedId === item.data.id;

    if (item.kind === "member") {
      const m = item.data;
      const ring = m.has_real ? "#FF3B30" : m.has_active ? "#0a0a0a" : null;
      return (
        <div key={"m-" + m.id} ref={setRef} onClick={() => openDetail(item)} style={{ flexShrink: 0, width: 82, textAlign: "center", cursor: "pointer" }}>
          <div style={{ position: "relative", width: 66, height: 66, margin: "0 auto 6px", borderRadius: "50%", border: isSel ? "2.5px solid #0a0a0a" : ring ? `2.5px solid ${ring}` : "2.5px solid transparent", padding: 2 }}>
            <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700 }}>
              {m.avatar_url ? <img src={m.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(m.name)}
            </div>
            {m.is_online && <div style={{ position: "absolute", bottom: 1, right: 1, width: 14, height: 14, borderRadius: "50%", backgroundColor: "#0a0a0a", border: "2px solid white" }} />}
          </div>
          {/* Miniature du carrousel replié : mélangée aux autres types, peu d'espace — prénom et
              note sociale seulement. La badge Passion/Power appartient à la fiche complète
              (renderDetailCard), pas ici. */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name.split(" ")[0]}</div>
            {!!m.is_verified && <VerifiedBadge size={11} />}
          </div>
          <div style={{ fontSize: 10, color: "#999" }}>{m.is_online ? "En ligne" : `★ ${formatScore(m.score)}`}</div>
        </div>
      );
    }

    // Un Live garde sa silhouette RECTANGULAIRE (format vidéo) jusque dans le carrousel, avec
    // l'aperçu de la vidéo et la pastille rouge "(live)" — même langage que la galerie de
    // Profile.tsx, et même forme que son marqueur sur la carte.
    if (item.kind === "live") {
      const lv = item.data;
      return (
        <div key={"live-" + lv.id} ref={setRef} onClick={() => openDetail(item)} style={{ flexShrink: 0, width: 156, height: 98, borderRadius: 14, position: "relative", overflow: "hidden", cursor: "pointer", border: isSel ? "2.5px solid #0a0a0a" : "2.5px solid transparent", boxShadow: "0 4px 14px rgba(0,0,0,0.12)", backgroundColor: "#0a0a0a" }}>
          <video src={lv.url} muted preload="metadata" playsInline style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          <span style={{ position: "absolute", top: 6, left: 6, fontSize: 9.5, fontWeight: 800, color: "white", backgroundColor: "#e74c3c", borderRadius: 5, padding: "2px 5px", letterSpacing: 0.5 }}>(live)</span>
          <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><polygon points="6,4 20,12 6,20" /></svg>
            </span>
          </span>
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "14px 8px 7px", background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 100%)", color: "white", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {lv.author_name}
          </div>
        </div>
      );
    }

    // Une publication (Événement / Passion / Power) est un LOSANGE partout, carrousel compris :
    // au milieu des ronds (profils, lieux) et des rectangles (Lives), on sait de quoi il s'agit
    // avant même d'avoir lu le titre.
    if (item.kind === "publication") {
      const p = item.data;
      return (
        <div key={"pub-" + p.id} ref={setRef} onClick={() => openDetail(item)} style={{ flexShrink: 0, width: 90, textAlign: "center", cursor: "pointer" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
            <LosangeThumb url={p.cover_url} side={58} accent={publicationAccent(p)} selected={isSel} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.title}</div>
          <div style={{ fontSize: 10, color: "#999", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.category || t(lang, "stories_link_event")}</div>
        </div>
      );
    }

    // Un Lieu garde sa silhouette CARRÉE jusque dans le carrousel, à la même échelle que le cercle
    // (profil) et le losange (publication) — plus le grand format poster qu'il partageait
    // auparavant avec le repli générique ci-dessous, qui l'écrasait au milieu d'une liste mélangée.
    if (item.kind === "venue") {
      const v = item.data;
      return (
        <div key={"venue-" + v.id} ref={setRef} onClick={() => openDetail(item)} style={{ flexShrink: 0, width: 82, textAlign: "center", cursor: "pointer" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
            <SquareThumb url={v.cover_url} side={62} selected={isSel} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.name}</div>
          <div style={{ fontSize: 10, color: "#999", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.type || "Lieu"}</div>
        </div>
      );
    }

    // Rituels et communautés partagent la même silhouette carrée que les Lieux — même principe
    // que ci-dessus, une photo réelle vaut mieux qu'un cercle de profil générique.
    if (item.kind === "ritual" || item.kind === "community") {
      const r = item.data;
      return (
        <div key={item.kind + "-" + r.id} ref={setRef} onClick={() => openDetail(item)} style={{ flexShrink: 0, width: 82, textAlign: "center", cursor: "pointer" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
            <SquareThumb url={r.cover_url} side={62} selected={isSel} kind={item.kind} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.title || r.name}</div>
          <div style={{ fontSize: 10, color: "#999", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.kind === "ritual" ? r.frequency : t(lang, "expl_members_count").replace("{n}", String(r.members_count || 0))}</div>
        </div>
      );
    }

    // FAST rejoint la silhouette losange (comme Passion/Power), pas le cercle des profils — même
    // langage que le marqueur de la carte. Film gris sur la photo pour que l'icône de catégorie
    // (café, verre…) reste lisible dessus, éclair doré au bas du losange.
    if (item.kind === "fast") {
      const q = item.data;
      return (
        <div key={"fast-" + q.id} ref={setRef} onClick={() => openDetail(item)} style={{ flexShrink: 0, width: 90, textAlign: "center", cursor: "pointer" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
            <FastLosangeThumb url={q.organizer_avatar} side={58} category={q.category} selected={isSel} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{FAST_CATEGORY_LABELS[q.category as FastCategoryKey] || "FAST"}</div>
          <div style={{ fontSize: 10, color: "#999", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fastLocationText(q)}</div>
        </div>
      );
    }

    // MOVE — cercle façon profil (avatar de l'organisateur), avec le pictogramme du mode en
    // repli s'il n'y a pas d'avatar — même langage que les autres silhouettes du carrousel.
    if (item.kind === "move") {
      const mv = item.data;
      const ModeIcon = MOVE_MODE_ICONS[(mv.move_mode as MoveMode) || "ride"];
      return (
        <div key={"move-" + mv.id} ref={setRef} onClick={() => openDetail(item)} style={{ flexShrink: 0, width: 90, textAlign: "center", cursor: "pointer" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
            <div style={{ position: "relative", width: 58, height: 58, borderRadius: "50%", border: isSel ? "3px solid #0a0a0a" : "3px solid white", boxShadow: "0 3px 10px rgba(0,0,0,0.2)", overflow: "hidden", backgroundColor: "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {mv.author_avatar ? <img src={mv.author_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ModeIcon size={24} color="#0a0a0a" />}
              <span style={{ position: "absolute", bottom: -3, right: -3, width: 22, height: 22, borderRadius: "50%", backgroundColor: "white", border: "1.5px solid #ECECEC", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.22)" }}>
                <ModeIcon size={13} color="#0a0a0a" />
              </span>
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mv.title}</div>
          <div style={{ fontSize: 10, color: "#999", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mv.destination_name}</div>
        </div>
      );
    }

    const d = item.data;
    const title = d.name || d.title;
    const badge = KIND_BADGE[item.kind] ? KIND_BADGE[item.kind](d) : "";
    return (
      <div key={item.kind + "-" + d.id} ref={setRef} onClick={() => openDetail(item)} style={{ flexShrink: 0, width: 148, height: 108, borderRadius: 16, position: "relative", overflow: "hidden", cursor: "pointer", border: isSel ? "2.5px solid #0a0a0a" : "2.5px solid transparent", boxShadow: "0 4px 14px rgba(0,0,0,0.12)" }}>
        <div style={{ position: "absolute", inset: 0, backgroundColor: d.cover_url ? "#0a0a0a" : "#F2F2F2", backgroundImage: d.cover_url ? `url(${d.cover_url})` : "none", backgroundSize: "cover", backgroundPosition: "center" }} />
        {!d.cover_url && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><Glyph kind={item.kind} size={30} color="#0a0a0a" /></div>}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.05) 35%, rgba(0,0,0,0.78) 100%)" }} />
        <div style={{ position: "absolute", top: 8, left: 8, backgroundColor: "rgba(255,255,255,0.92)", borderRadius: 8, padding: "2.5px 8px", fontSize: 9, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "#0a0a0a" }}>{badge}</div>
        <div style={{ position: "absolute", bottom: 8, left: 9, right: 9, color: "white", fontSize: 12.5, fontWeight: 700, lineHeight: 1.25, textShadow: "0 1px 4px rgba(0,0,0,0.5)", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const }}>{title}</div>
      </div>
    );
  };

  // Ligne pleine largeur pour la liste étendue (tiroir tiré vers le haut) — plus grande, plus
  // simple à toucher qu'une carte compacte, pour parcourir tranquillement de nombreux résultats.
  const renderListRow = (item: { kind: string; data: any }) => {
    const isSel = selectedId === item.data.id;
    const setRef = (el: HTMLDivElement | null) => { cardRefs.current[item.data.id] = el; };
    const dist = distanceLabel(item.data, center);
    const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, padding: "12px 4px", borderBottom: "1px solid #F5F5F5", cursor: "pointer", backgroundColor: isSel ? "#F8F8F8" : "transparent", borderRadius: 12 };
    const thumb = (kind: string, img?: string) => (
      <div style={{ width: 50, height: 50, borderRadius: 14, flexShrink: 0, backgroundColor: img ? "#0a0a0a" : "#F2F2F2", backgroundImage: img ? `url(${img})` : "none", backgroundSize: "cover", backgroundPosition: "center", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {!img && <Glyph kind={kind} size={22} />}
      </div>
    );

    const GlobeIcon = ({ size = 10 }: { size?: number }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    );
    const LockIcon = ({ size = 10 }: { size?: number }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    );
    const PeopleIcon = ({ size = 10 }: { size?: number }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
    // Disposition façon "Entre Potes" demandée par le fondateur pour Communauté/Rituel — photo
    // carrée (thumb() ci-dessus, déjà en place) + un badge "Public"/"Sur invitation" (icône
    // globe/cadenas) suivi d'un badge compteur, à la place du texte gris plat d'avant. Couleurs
    // OLTODO plutôt que le jaune de la capture d'écran, jugé hors palette.
    const audienceBadgeRow = (isPublic: boolean, count: number, extra?: string) => (
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" as const }}>
        <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: "#999" }}>
          {isPublic ? <GlobeIcon size={10} /> : <LockIcon size={10} />} {isPublic ? "Public" : "Sur invitation"}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 3, borderRadius: 10, padding: "2px 7px", backgroundColor: "#F0F0F0", fontSize: 11, fontWeight: 700, color: "#0a0a0a" }}>
          <PeopleIcon size={10} /> {count}
        </span>
        {!!extra && <span style={{ fontSize: 12, color: "#999" }}>{extra}</span>}
      </div>
    );

    if (item.kind === "member") {
      const m = item.data;
      const ring = m.has_real ? "#FF3B30" : m.has_active ? "#0a0a0a" : "transparent";
      return (
        <div key={m.id} ref={setRef} onClick={() => openDetail(item)} style={rowStyle}>
          <div style={{ position: "relative", flexShrink: 0, padding: 2.5, border: `2.5px solid ${ring}`, borderRadius: "50%" }}>
            <div style={{ width: 50, height: 50, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700 }}>
              {m.avatar_url ? <img src={m.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(m.name)}
            </div>
            {m.is_online && <div style={{ position: "absolute", bottom: 0, right: 0, width: 13, height: 13, borderRadius: "50%", backgroundColor: "#0a0a0a", border: "2px solid white" }} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name}</div>
              {!!m.is_verified && <VerifiedBadge size={13} />}
            </div>
            <div style={{ fontSize: 12, color: "#999" }}>{dist ? `${dist} · ` : ""}{m.city || "—"} · ★ {formatScore(m.score)}</div>
            {(m.passions?.length > 0 || m.powers?.length > 0) && (
              <div style={{ display: "flex", gap: 4, marginTop: 4, overflow: "hidden" }}>
                {(m.passions || []).slice(0, 1).map((p: string) => (
                  <span key={"p-" + p} style={{ fontSize: 10, fontWeight: 600, color: "#0a0a0a", backgroundColor: "#F0F0F0", borderRadius: 10, padding: "2px 8px", whiteSpace: "nowrap" }}>{tTax(lang, p)}</span>
                ))}
                {(m.powers || []).slice(0, 1).map((p: string) => (
                  <span key={"pw-" + p} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 600, color: "#0a0a0a", backgroundColor: "#F0F0F0", borderRadius: 10, padding: "2px 8px", whiteSpace: "nowrap" }}>
                    <PowerIcon size={9} color="#0a0a0a" /> {tTax(lang, p)}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button onClick={e => { e.stopPropagation(); navigate(`/profile/${m.id}`); }} style={{ padding: "8px 14px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0, alignSelf: "center" }}>{t(lang, "prof_tab_profile")}</button>
        </div>
      );
    }
    if (item.kind === "venue") {
      const v = item.data;
      return (
        <div key={v.id} ref={setRef} onClick={() => openDetail(item)} style={rowStyle}>
          {thumb("venue", v.cover_url)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.name}</div>
              {v.is_sponsored && v.boosted_until && new Date(v.boosted_until) > new Date() && (
                <span style={{ fontSize: 10, fontWeight: 700, color: "#8a6d1a", backgroundColor: "#FFF7E0", border: "1px solid #F0D98C", borderRadius: 10, padding: "1px 7px", flexShrink: 0 }}>{t(lang, "pc_sponsored")}</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#999" }}>{dist ? `${dist} · ` : ""}{v.type || "Lieu"}</div>
          </div>
          <button onClick={e => { e.stopPropagation(); navigate(`/venue/${v.id}`); }} style={{ padding: "8px 14px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>{t(lang, "expl_view")}</button>
        </div>
      );
    }
    if (item.kind === "fast") {
      const q = item.data;
      return (
        <div key={q.id} ref={setRef} onClick={() => openDetail(item)} style={rowStyle}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div style={{ width: 50, height: 50, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700 }}>
              {q.organizer_avatar ? <img src={q.organizer_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(q.organizer_name)}
            </div>
            <div style={{ position: "absolute", top: -2, left: -2, width: 18, height: 18, borderRadius: "50%", backgroundColor: "#0a0a0a", border: `1.5px solid ${FAST_COLOR}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Glyph kind="fast" size={9} color={FAST_COLOR} />
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{FAST_CATEGORY_LABELS[q.category as FastCategoryKey] || "FAST"} · {q.organizer_name}</div>
            <div style={{ fontSize: 12, color: "#999" }}>{dist ? `${dist} · ` : ""}{fastLocationText(q)}</div>
          </div>
          <button onClick={e => { e.stopPropagation(); openDetail(item); }} style={{ padding: "8px 14px", borderRadius: 20, border: `1.5px solid ${FAST_COLOR}`, backgroundColor: "#0a0a0a", color: FAST_COLOR, fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>{t(lang, "expl_view")}</button>
        </div>
      );
    }
    if (item.kind === "move") {
      const mv = item.data;
      const ModeIcon = MOVE_MODE_ICONS[(mv.move_mode as MoveMode) || "ride"];
      return (
        <div key={mv.id} ref={setRef} onClick={() => openDetail(item)} style={rowStyle}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div style={{ width: 50, height: 50, borderRadius: "50%", overflow: "hidden", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {mv.author_avatar ? <img src={mv.author_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ModeIcon size={22} color="#0a0a0a" />}
            </div>
            <div style={{ position: "absolute", bottom: -2, right: -2, width: 18, height: 18, borderRadius: "50%", backgroundColor: "white", border: "1.5px solid #ECECEC", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ModeIcon size={10} color="#0a0a0a" />
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mv.location_name} → {mv.destination_name}</div>
            <div style={{ fontSize: 12, color: "#999" }}>{dist ? `${dist} · ` : ""}{mv.event_date && new Date(mv.event_date).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
          </div>
          <button onClick={e => { e.stopPropagation(); openDetail(item); }} style={{ padding: "8px 14px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>{t(lang, "expl_view")}</button>
        </div>
      );
    }
    if (item.kind === "community") {
      const c = item.data;
      return (
        <div key={c.id} ref={setRef} onClick={() => openDetail(item)} style={rowStyle}>
          {thumb("community", c.cover_url)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
            {audienceBadgeRow(c.adhesion === "public", c.members_count || 0)}
          </div>
          <button onClick={e => { e.stopPropagation(); navigate(`/community/${c.id}`); }} style={{ padding: "8px 14px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>{t(lang, "expl_view")}</button>
        </div>
      );
    }
    if (item.kind === "ritual") {
      const r = item.data;
      return (
        <div key={r.id} ref={setRef} onClick={() => openDetail(item)} style={rowStyle}>
          {thumb("ritual", r.cover_url)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.title}</div>
            {audienceBadgeRow(r.audience === "public", r.members_count || 0, r.frequency)}
          </div>
          <button onClick={e => { e.stopPropagation(); navigate(`/ritual/${r.id}`); }} style={{ padding: "8px 14px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>{t(lang, "expl_view")}</button>
        </div>
      );
    }
    // Ligne d'un Live : vignette carrée (première image de la vidéo), auteur, fraîcheur, vues et
    // tops — les repères qui disent s'il vaut la peine d'être regardé maintenant.
    if (item.kind === "live") {
      const lv = item.data;
      return (
        <div key={lv.id} ref={setRef} onClick={() => openDetail(item)} style={rowStyle}>
          <div style={{ width: 50, height: 50, borderRadius: 14, flexShrink: 0, overflow: "hidden", position: "relative", backgroundColor: "#0a0a0a" }}>
            <video src={lv.url} muted preload="metadata" playsInline style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="white"><polygon points="6,4 20,12 6,20" /></svg>
              </span>
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lv.author_name}</div>
              <span style={{ fontSize: 9.5, fontWeight: 800, color: "white", backgroundColor: "#e74c3c", borderRadius: 5, padding: "2px 5px", letterSpacing: 0.5, flexShrink: 0 }}>(live)</span>
            </div>
            <div style={{ fontSize: 12, color: "#999" }}>
              {dist ? `${dist} · ` : ""}{timeAgo(lv.created_at)}
              {Number(lv.views_count) > 0 ? ` · ${t(lang, "expl_views_count").replace("{n}", String(lv.views_count))}` : ""}
              {Number(lv.likes_count) > 0 ? ` · ★ ${lv.likes_count}` : ""}
            </div>
          </div>
          <button onClick={e => { e.stopPropagation(); setLiveLightbox(lv); }} style={{ padding: "8px 14px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>{t(lang, "expl_view")}</button>
        </div>
      );
    }
    // Publication : vignette en losange, comme son marqueur — la ligne de liste et le point sur
    // la carte se répondent, on ne cherche pas lequel correspond à l'autre.
    const p = item.data;
    return (
      <div key={p.id} ref={setRef} onClick={() => openDetail(item)} style={rowStyle}>
        <div style={{ width: 50, display: "flex", justifyContent: "center", flexShrink: 0 }}>
          <LosangeThumb url={p.cover_url} side={36} accent={publicationAccent(p)} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.title}</div>
          <div style={{ fontSize: 12, color: "#999" }}>{p.author_name} · {(p.is_free || Number(p.price) === 0) ? t(lang, "price_free") : `${p.price}€`}</div>
        </div>
        <button onClick={e => { e.stopPropagation(); navigate(`/publication/${p.id}`); }} style={{ padding: "8px 14px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>{t(lang, "expl_view")}</button>
      </div>
    );
  };

  // FICHE D'UN SEUL ÉLÉMENT — ce qu'un tap sur la carte montre désormais, immédiatement : la
  // photo en grand, le nom, les quelques faits qui comptent pour ce type-là, l'action principale,
  // et seulement ensuite la porte vers la liste ("Voir les autres …"). L'ordre est volontaire :
  // on répond d'abord à "c'est quoi, ça ?", jamais par un mur de résultats.
  const renderDetailCard = (item: MapPoint) => {
    const d = item.data;
    const dist = distanceLabel(d, center);
    const facts: React.ReactNode[] = [];
    // Badges Passion/Power — uniquement pour un profil (voir plus bas), rendus en petites puces
    // séparées de la ligne de "facts" plutôt qu'entremêlés avec elle.
    const badges: string[] = [];
    let title = d.name || d.title || "";
    let primary: { label: string; onClick: () => void } | null = null;
    let media: React.ReactNode = null;

    const cover = (url?: string) => (
      <div style={{ width: "100%", height: 150, borderRadius: 16, overflow: "hidden", backgroundColor: url ? "#0a0a0a" : "#F2F2F2", backgroundImage: url ? `url(${url})` : "none", backgroundSize: "cover", backgroundPosition: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {!url && <Glyph kind={item.kind} size={44} color="#0a0a0a" />}
      </div>
    );

    if (item.kind === "member") {
      media = (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ width: 108, height: 108, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 30 }}>
            {d.avatar_url ? <img src={d.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(d.name)}
          </div>
        </div>
      );
      // Nom complet (pas juste le prénom du carrousel replié, cette fiche a la place) + note
      // sociale + Passions/Powers en petits badges — à la différence de la miniature du
      // carrousel (mélangée à d'autres types, trop à l'étroit pour ça), cette fiche prend toute
      // la place du tiroir : rien n'empêche d'y montrer ce qui rend vraiment ce profil concret.
      title = d.name;
      facts.push(`★ ${formatScore(d.score)}`);
      (d.passions || []).forEach((p: string) => badges.push(tTax(lang, p)));
      (d.powers || []).forEach((p: string) => badges.push(`⚡ ${tTax(lang, p)}`));
      primary = { label: t(lang, "see_profile"), onClick: () => navigate(`/profile/${d.id}`) };
    } else if (item.kind === "venue") {
      media = cover(d.cover_url);
      facts.push(d.type || t(lang, "publish_as_type_venue"));
      if (d.is_sponsored && d.boosted_until && new Date(d.boosted_until) > new Date()) facts.push(t(lang, "pc_sponsored"));
      primary = { label: t(lang, "expl_view_venue"), onClick: () => navigate(`/venue/${d.id}`) };
    } else if (item.kind === "ritual") {
      media = cover(d.cover_url);
      facts.push(d.frequency || t(lang, "publish_as_type_ritual"), t(lang, "expl_participants_count").replace("{n}", String(d.members_count || 0)));
      primary = { label: t(lang, "expl_view_ritual"), onClick: () => navigate(`/ritual/${d.id}`) };
    } else if (item.kind === "community") {
      media = cover(d.cover_url);
      facts.push(t(lang, "expl_members_count").replace("{n}", String(d.members_count || 0)));
      primary = { label: t(lang, "expl_view_community"), onClick: () => navigate(`/community/${d.id}`) };
    } else if (item.kind === "live") {
      // Un Live s'ouvre dans la visionneuse déjà utilisée partout ailleurs (PhotoLightbox) plutôt
      // que dans un lecteur inventé pour l'occasion : même plein écran, mêmes actions.
      title = d.author_name;
      media = (
        <div onClick={() => setLiveLightbox(d)} style={{ position: "relative", width: "100%", height: 170, borderRadius: 16, overflow: "hidden", backgroundColor: "#0a0a0a", cursor: "pointer" }}>
          <video src={d.url} muted preload="metadata" playsInline style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          <span style={{ position: "absolute", top: 8, left: 8, fontSize: 10.5, fontWeight: 800, color: "white", backgroundColor: "#e74c3c", borderRadius: 6, padding: "2px 6px", letterSpacing: 0.5 }}>{t(lang, "home_live_pill")}</span>
          <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ width: 52, height: 52, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><polygon points="6,4 20,12 6,20" /></svg>
            </span>
          </span>
        </div>
      );
      facts.push(timeAgo(d.created_at));
      if (d.location_name) facts.push(d.location_name);
      if (Number(d.views_count) > 0) facts.push(t(lang, "expl_views_count").replace("{n}", String(d.views_count)));
      primary = { label: t(lang, "expl_view_video"), onClick: () => setLiveLightbox(d) };
    } else if (item.kind === "fast") {
      // Pas de "Voir la fiche" générique ici : le bouton principal EST l'action ("J'ARRIVE"),
      // rendu séparément plus bas (état joined/count tenu au niveau du composant, pas ici — voir
      // le commentaire sur arrivedFastIds/fastParticipantCounts). Badge éclair fin (contour, pas
      // un aplat) — même retenue que le losange de la carte.
      media = (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ position: "relative", width: 108, height: 108 }}>
            <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 30 }}>
              {d.organizer_avatar ? <img src={d.organizer_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(d.organizer_name)}
            </div>
            <div style={{ position: "absolute", top: -3, left: -3, width: 34, height: 34, borderRadius: "50%", backgroundColor: "#0a0a0a", border: `2px solid ${FAST_COLOR}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Glyph kind="fast" size={15} color={FAST_COLOR} />
            </div>
          </div>
        </div>
      );
      title = `${FAST_CATEGORY_LABELS[d.category as FastCategoryKey] || "FAST"} · ${d.organizer_name}`;
      const expiresAt = new Date(d.expires_at);
      const minsLeft = Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / 60000));
      const remaining = minsLeft <= 0 ? t(lang, "pc_finished") : minsLeft < 60 ? t(lang, "expl_mins_left").replace("{n}", String(minsLeft)) : t(lang, "expl_until").replace("{t}", expiresAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }));
      facts.push(fastLocationText(d), new Date(d.starts_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }), remaining);
      const count = fastParticipantCounts[d.id] ?? d.participants_count ?? 0;
      if (count > 0) facts.push(t(lang, "expl_persons_count").replace("{n}", String(count)));
    } else if (item.kind === "move") {
      // Pas de participation/"On My Way" ici : la logique complète (paiement Stripe éventuel,
      // toggle "Je suis en route") vit dans MoveDetailModal, déjà utilisée par pages/Move.tsx —
      // le bouton principal ouvre cette même modale plutôt que de la dupliquer dans cette fiche
      // générique, qui gère une dizaine de types de contenus très différents.
      const ModeIcon = MOVE_MODE_ICONS[(d.move_mode as MoveMode) || "ride"];
      media = (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ position: "relative", width: 108, height: 108 }}>
            <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {d.author_avatar ? <img src={d.author_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ModeIcon size={40} color="#0a0a0a" />}
            </div>
            <div style={{ position: "absolute", bottom: -3, right: -3, width: 34, height: 34, borderRadius: "50%", backgroundColor: "white", border: "2px solid #ECECEC", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ModeIcon size={17} color="#0a0a0a" />
            </div>
          </div>
        </div>
      );
      title = `${d.location_name} → ${d.destination_name}`;
      facts.push(moveModeLabel((d.move_mode as MoveMode) || "ride"));
      if (d.event_date) facts.push(new Date(d.event_date).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }));
      facts.push(d.current_places >= d.max_places ? t(lang, "move_full") : `${d.current_places}/${d.max_places} ${t(lang, "places")}`);
      facts.push((d.is_free || Number(d.price) === 0) ? t(lang, "price_free") : t(lang, "expl_price_per_place").replace("{price}", String(d.price)));
      primary = { label: t(lang, "move_view_trip"), onClick: () => setMoveDetailId(d.id) };
    } else {
      // La fiche d'une publication reprend sa silhouette de carte : on retrouve exactement le
      // losange qu'on vient de toucher, accent compris.
      media = (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LosangeThumb url={d.cover_url} side={104} accent={publicationAccent(d)} />
        </div>
      );
      facts.push(d.author_name || "—", (d.is_free || Number(d.price) === 0) ? t(lang, "price_free") : `${d.price}€`);
      primary = { label: t(lang, "expl_view_publication"), onClick: () => navigate(`/publication/${d.id}`) };
    }
    // La distance reste pertinente pour situer un lieu/rituel/communauté/publication/live, mais
    // pas pour un profil : la fiche profil doit rester strictement photo + nom + note sociale.
    if (dist && item.kind !== "member") facts.unshift(dist);

    return (
      <div className="no-scrollbar" style={{ flex: 1, overflowY: "auto", padding: "0 14px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0 10px" }}>
          <div onClick={closeDetail} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#0a0a0a" }}>{t(lang, "expl_back")}</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5 }}>{KIND_LABELS[item.kind]}</div>
        </div>
        {media}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.25 }}>{title}</div>
          {item.kind === "member" && !!d.is_verified && <VerifiedBadge size={16} />}
        </div>
        <div style={{ fontSize: 13, color: "#777", marginTop: 5, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {facts.filter(Boolean).map((f, i) => (
            <span key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {i > 0 && <span style={{ color: "#DDD" }}>·</span>}{f}
            </span>
          ))}
        </div>
        {badges.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
            {badges.map((b, i) => {
              const isPower = b.startsWith("⚡ ");
              return (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: "#0a0a0a", backgroundColor: "#F0F0F0", border: "1px solid #E5E5E5", borderRadius: 20, padding: "4px 11px" }}>
                  {isPower && <PowerIcon size={11} color="#0a0a0a" />}
                  {isPower ? b.slice(2) : b}
                </span>
              );
            })}
          </div>
        )}
        {item.kind === "fast" && !!d.message && (
          <div style={{ fontSize: 14, color: "#333", marginTop: 12, fontStyle: "italic" }}>« {d.message} »</div>
        )}
        {/* Lieu — même mini-carte que post.venue_name dans PostCard.tsx quand le FAST est
            rattaché à un lieu OLTODO certifié (ex: Le Regina). Sinon (position GPS ou adresse
            libre : la grande majorité des FAST), le nom du lieu seul ("Ma position actuelle")
            ne dit rien à qui doit s'y rendre — un lien Google Maps direct est ce qui rend
            l'information réellement actionnable. */}
        {item.kind === "fast" && d.venue_name && (
          <div onClick={() => navigate(`/venue/${d.venue_id}`)} style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, padding: "10px 12px", borderRadius: 14, backgroundColor: "#F8F8F8", border: "1px solid #F0F0F0", cursor: "pointer" }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: "#0a0a0a", backgroundImage: d.venue_avatar ? `url(${d.venue_avatar})` : "none", backgroundSize: "cover", backgroundPosition: "center", flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.venue_name}</span>
                {d.venue_verified && <VerifiedBadge size={13} />}
              </div>
              <div style={{ fontSize: 11, color: "#999" }}>{d.venue_verified ? t(lang, "pc_venue_certified") : (d.venue_type || t(lang, "publish_as_type_venue"))}</div>
            </div>
            <span style={{ fontSize: 11, color: "#0a0a0a", fontWeight: 600, flexShrink: 0 }}>{t(lang, "pc_see_arrow")}</span>
          </div>
        )}
        {/* Itinéraire — n'apparaît QUE si l'API a effectivement renvoyé l'adresse exacte
            (organisateur, ou après avoir cliqué J'ARRIVE) : avant ça, seuls la ville/le quartier
            sont connus, un lien Maps serait soit muet soit une fuite de la position précise via
            les coordonnées brutes. La query utilise l'adresse texte (plus fiable qu'un lien
            lat/lng brut pour une adresse libre géocodée) plutôt que latitude/longitude. */}
        {item.kind === "fast" && !d.venue_name && !!d.exact_address && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(d.exact_address)}`}
            target="_blank" rel="noreferrer"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 12, padding: "10px 12px", borderRadius: 14, backgroundColor: "#F8F8F8", border: "1px solid #F0F0F0", color: "#0a0a0a", fontSize: 13, fontWeight: 700, textDecoration: "none" }}
          >
            <PinIcon color="#0a0a0a" size={15} /> Itinéraire, {d.exact_address}
          </a>
        )}
        {item.kind === "fast" && !d.venue_name && !d.exact_address && (
          <div style={{ fontSize: 12, color: "#999", marginTop: 10, textAlign: "center" }}>
            L'adresse exacte et l'itinéraire arrivent après avoir cliqué sur « J'ARRIVE »
          </div>
        )}
        {item.kind === "fast" ? (
          d.user_id === user?.id ? (
            <div style={{ width: "100%", marginTop: 14, padding: "14px 0", borderRadius: 14, border: "1.5px solid #E0E0E0", textAlign: "center", color: "#999", fontSize: 13.5, fontWeight: 600 }}>
              C'est votre FAST
            </div>
          ) : (
          <button
            onClick={() => arriveAtFast(d.id)}
            disabled={arrivedFastIds.has(d.id)}
            style={{
              width: "100%", marginTop: 14, padding: "14px 0", borderRadius: 14,
              border: arrivedFastIds.has(d.id) ? "1.5px solid #E0E0E0" : `1.5px solid ${FAST_COLOR}`,
              backgroundColor: arrivedFastIds.has(d.id) ? "#F0F0F0" : "#0a0a0a",
              color: arrivedFastIds.has(d.id) ? "#666" : "white",
              fontSize: 15, fontWeight: 800, cursor: arrivedFastIds.has(d.id) ? "default" : "pointer",
            }}
          >
            {arrivedFastIds.has(d.id) ? "✓ J'arrive" : "J'ARRIVE"}
          </button>
          )
        ) : primary && (
          <button onClick={primary.onClick} style={{ width: "100%", marginTop: 14, padding: "13px 0", borderRadius: 14, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
            {primary.label}
          </button>
        )}
        <button onClick={() => showOthersOf(item.kind)} style={{ width: "100%", marginTop: 9, padding: "13px 0", borderRadius: 14, border: "1.5px solid #E4E4E4", backgroundColor: "white", color: "#0a0a0a", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          {t(lang,"explore_see_others")||"Voir les autres"} {KIND_PLURAL[item.kind]}
        </button>
      </div>
    );
  };

  const KIND_LABELS: Record<string, string> = {
    member: t(lang,"explore_kind_member")||"Personnes", venue: t(lang,"explore_tab_venues")||"Lieux",
    community: t(lang,"explore_tab_communities")||"Communautés", ritual: t(lang,"explore_tab_rituals")||"Rituels",
    publication: t(lang,"explore_kind_publication")||"Publications", live: t(lang,"explore_tab_lives")||"Lives", fast: t(lang,"explore_tab_fasts")||"Fasts",
    move: t(lang,"explore_tab_moves")||"Move",
  };
  const groupedForExpanded = useMemo(() => {
    const groups: Record<string, { kind: string; data: any }[]> = {};
    drawerItems.forEach(it => { (groups[it.kind] = groups[it.kind] || []).push(it); });
    return groups;
  }, [drawerItems]);

  // La densité se mesure désormais sur TOUT ce qui est affiché, pas seulement sur les profils :
  // c'est l'ensemble des marqueurs qui encombre la carte, quel que soit leur type.
  const markerBaseSize = scaledMemberSize(mapPoints.length, isMobile);
  const clusterRadius = scaledClusterRadius(mapPoints.length, isMobile ? 66 : 46);

  // TROIS silhouettes, une seule règle : cercle photo par défaut (profils, lieux, rituels,
  // communautés), rectangle vidéo pour les Lives, losange pour les publications
  // (Événement/Passion/Power). L'anneau noir signale un ami ou l'élément sélectionné, l'anneau
  // blanc tout le reste — convention déjà en place pour les profils, étendue telle quelle.
  const markerIcon = (point: MapPoint) => {
    const d = point.data;
    const isSel = selectedId === d.id;
    // Anneau or (#b8952e, accent OLTODO déjà utilisé partout ailleurs) : ce membre/lieu propose
    // le Power recherché avec un agenda déjà ouvert — réellement réservable, pas juste listé. Un
    // membre renvoyé par /users/discover?power=X a par construction un agenda (le backend
    // l'exige désormais), donc tout membre affiché quand powerFilter est actif en est la preuve.
    const isReservableForPower = !!powerFilter && (point.kind === "member" || d.is_power_provider);
    const ring = isSel || d.is_friend ? "#0a0a0a" : (isReservableForPower ? "#b8952e" : "white");
    const size = isSel ? markerBaseSize + 10 : markerBaseSize;
    const pad = isMobile ? 8 : 4;
    const seed = String(d.id || "");
    if (point.kind === "live") return liveRectIcon(d.url, ring, size, pad, seed);
    if (point.kind === "publication") return losangeIcon(d.cover_url, ring, size, pad, publicationAccent(d), seed);
    // Rituels et communautés partagent désormais la silhouette carrée des Lieux (même repère
    // visuel : "ce contenu a une vraie photo, pas juste un profil"), sans les anneaux Vibe/Real
    // qui restent une signalisation propre aux Lieux.
    if (point.kind === "venue") return photoSquareIcon(d.cover_url, ring, size, pad, seed, !!d.has_vibe_content, !!d.has_real_content, "venue", d.average_rating != null ? Number(d.average_rating) : null);
    if (point.kind === "ritual" || point.kind === "community") return photoSquareIcon(d.cover_url, ring, size, pad, seed, false, false, point.kind);
    if (point.kind === "fast") return fastLosangeIcon(size, pad, seed, isSel);
    if (point.kind === "move") return moveIcon(size, pad, d.move_mode || "ride", seed, isSel);
    const photo = point.kind === "member" ? d.avatar_url : d.cover_url;
    const contentRingKind = point.kind === "member" ? (d.has_real ? "real" : d.has_active ? "vibe" : null) : null;
    return photoCircleIcon(point.kind, photo, ring, size, pad, seed, contentRingKind);
  };

  // Base commune du "colonne de droite" (radius/recentrer/ajouter un lieu) — descend de 44px dès
  // qu'une rangée de sous-filtres (passions/powers/move/lieux) occupe la ligne du dessous.
  const rightStackTop = (showMembers || showMoves || showVenues) ? 104 : 60;

  return (
    <div style={{ height: "100%", position: "relative", overflow: "hidden" }}>

      {/* Carte des amis — volontairement un simple lien flottant plutôt qu'intégré aux
          filtres/tiroir existants ci-dessous : ce fil est déjà complexe, et la carte des amis
          vit sur sa propre page (voir FriendsMap.tsx) pour ne rien risquer ici. */}
      <button
        onClick={() => navigate("/friends-map")}
        title="Carte des amis"
        style={{ position: "absolute", bottom: 20, right: 14, zIndex: 1000, width: 48, height: 48, borderRadius: 24, border: "none", cursor: "pointer", backgroundColor: "#0a0a0a", color: "white", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(0,0,0,0.25)" }}
      >
        <Users size={20} strokeWidth={1.8} color="white" />
      </button>

      {/* FILTRES CATÉGORIE */}
      <div className="no-scrollbar" style={{ position: "absolute", top: 14, left: 14, right: 14, zIndex: 1000, display: "flex", gap: 8, overflowX: "auto", overflowY: "hidden", touchAction: "pan-x" }}>
        {/* "Tout" n'est plus une catégorie parmi d'autres : c'est le retour rapide à "toutes
            cochées". Les autres puces se cochent/décochent librement et se cumulent. */}
        <button onClick={selectAllCategories} style={{
          flexShrink: 0, padding: "9px 16px", borderRadius: 20, border: "none", cursor: "pointer",
          backgroundColor: isAll ? "#0a0a0a" : "white", color: isAll ? "white" : "#333",
          fontSize: 13, fontWeight: 600, boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
        }}>
          {t(lang,"explore_all")||"Tout"}
        </button>
        {CATEGORIES.map(c => {
          const on = !isAll && selected.has(c.key);
          return (
            <button key={c.key} onClick={() => toggleCategory(c.key)} style={{
              flexShrink: 0, padding: "9px 16px", borderRadius: 20, border: "none", cursor: "pointer",
              backgroundColor: on ? "#0a0a0a" : "white",
              color: on ? "white" : "#333",
              fontSize: 13, fontWeight: 600, boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
            }}>
              {t(lang, CATEGORY_I18N_KEY[c.key]) || c.label}
            </button>
          );
        })}
      </div>

      {/* SOUS-FILTRES PASSIONS / POWERS / MOVE / LIEUX — une seule rangée : deux <div> distincts
          positionnés au même endroit se superposaient dès que "Tout" affichait Membres ET Move
          simultanément, faisant disparaître les puces passions/powers sous celles de Move. */}
      {(showMembers || showMoves || showVenues) && (
        <div className="no-scrollbar" style={{ position: "absolute", top: 60, left: 14, right: 14, zIndex: 1000, display: "flex", gap: 6, overflowX: "auto", overflowY: "hidden", touchAction: "pan-x" }}>
          {showVenues && (
            <>
              {venueFamilyFilter ? (
                <button onClick={() => setVenueFamilyFilter(null)} style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 16, border: "none", cursor: "pointer", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 600 }}>
                  ✓ {tTax(lang, venueFamilyFilter)} ×
                </button>
              ) : (
                VENUE_TYPE_FAMILIES.filter(f => f.family !== "Autre").map(f => (
                  <button key={f.family} onClick={() => setVenueFamilyFilter(f.family)} style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 16, border: "1px solid #E0E0E0", cursor: "pointer", backgroundColor: "white", color: "#555", fontSize: 12, fontWeight: 500 }}>
                    {tTax(lang, f.family)}
                  </button>
                ))
              )}
            </>
          )}
          {showMembers && (
            <>
              {passionFilter && (
                <button onClick={() => setPassionFilter(null)} style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 16, border: "none", cursor: "pointer", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 600 }}>
                  ✓ {tTax(lang, passionFilter)} ×
                </button>
              )}
              {powerFilter && (
                <button onClick={() => setPowerFilter(null)} style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 16, border: "none", cursor: "pointer", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 600 }}>
                  ✓ {tTax(lang, powerFilter)} ×
                </button>
              )}
              {!passionFilter && nearbyPassions.slice(0, 6).map(p => (
                <button key={p} onClick={() => { setPassionFilter(p); setSelected(new Set(["members"])); }} style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 16, border: "1px solid #E0E0E0", cursor: "pointer", backgroundColor: "white", color: "#555", fontSize: 12, fontWeight: 500 }}>
                  {tTax(lang, p)}
                </button>
              ))}
              {!powerFilter && nearbyPowers.slice(0, 4).map(p => (
                <button key={p} onClick={() => { setPowerFilter(p); setSelected(new Set(["members"])); }} style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 16, border: "1px solid #E0E0E0", cursor: "pointer", backgroundColor: "white", color: "#555", fontSize: 12, fontWeight: 500 }}>
                  <PowerIcon size={11} color="#555" /> {tTax(lang, p)}
                </button>
              ))}
            </>
          )}
          {showMoves && nearbyMoveModes.map(m => {
            const Icon = MOVE_MODE_ICONS[m];
            const active = moveModeFilter.has(m);
            return (
              <button key={m} onClick={() => toggleMoveMode(m)} style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 16, border: active ? "none" : "1px solid #E0E0E0", cursor: "pointer", backgroundColor: active ? "#0a0a0a" : "white", color: active ? "white" : "#555", fontSize: 12, fontWeight: active ? 600 : 500 }}>
                <Icon size={12} color={active ? "white" : "#555"} /> {t(lang, MOVE_MODE_I18N_KEY[m])}
              </button>
            );
          })}
        </div>
      )}

      {/* Bouton périmètre — au-dessus de "recentrer"/"ajouter un lieu" (même colonne, même base
          que ces deux-là, voir rightStackTop) : révèle la jauge façon Tinder ci-dessous. */}
      <button onClick={() => setShowRadiusGauge(v => !v)} title="Régler le périmètre de recherche" style={{
        position: "absolute", top: rightStackTop, right: 14, zIndex: 1000,
        width: 40, height: 40, borderRadius: "50%", border: "none", cursor: "pointer",
        backgroundColor: showRadiusGauge ? "#0a0a0a" : "white", boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
        display: "flex", alignItems: "center", justifyContent: "center", transition: "background-color 0.15s ease",
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={showRadiusGauge ? "white" : "#0a0a0a"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9"/>
          <path d="M12 12l5-3"/>
          <circle cx="12" cy="12" r="1.4" fill={showRadiusGauge ? "white" : "#0a0a0a"} stroke="none"/>
        </svg>
      </button>

      {showRadiusGauge && (
        <div style={{
          position: "absolute", top: rightStackTop, right: 62, zIndex: 1000,
          width: 230, padding: "12px 16px", borderRadius: 20, backgroundColor: "white",
          boxShadow: "0 2px 10px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column", gap: 6,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={{ fontSize: 13, color: "#777" }}>{t(lang, "explore_radius_label") || "Périmètre"}</span>
            <span style={{ fontSize: 15, fontWeight: 600, color: "#0a0a0a", fontVariantNumeric: "tabular-nums" }}>{searchRadiusKm} km</span>
          </div>
          <input
            type="range" min={1} max={100} step={1} value={searchRadiusKm}
            onChange={(e) => setSearchRadiusKm(Number(e.target.value))}
            style={{ width: "100%", accentColor: "#0a0a0a" }}
          />
        </div>
      )}

      {/* Bouton recentrer sur ma position */}
      <button onClick={recenter} title="Recentrer sur ma position" style={{
        position: "absolute", top: rightStackTop + 48, right: 14, zIndex: 1000,
        width: 40, height: 40, borderRadius: "50%", border: "none", cursor: "pointer",
        backgroundColor: "white", boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/>
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
        </svg>
      </button>

      {/* Bouton FAST — badge circulaire inspiré de l'exemple fourni (anneau doré fin + symbole
          lumineux sur fond noir), adapté à la retenue OLTODO : pas de néon, juste un anneau très
          fin et une lueur discrète. Même éclair que le losange de la carte, pour une signature
          visuelle unique (bouton = marqueur = badge des cards). Légère respiration lumineuse au
          repos + un resserrement au survol, pour rester "ludique" sans devenir tape-à-l'œil. */}
      <style>{`
        @keyframes oltodo-fast-glow { 0%, 100% { box-shadow: 0 3px 12px rgba(0,0,0,0.25), 0 0 8px ${FAST_COLOR}4D; } 50% { box-shadow: 0 3px 12px rgba(0,0,0,0.25), 0 0 14px ${FAST_COLOR}80; } }
        .oltodo-fast-btn { animation: oltodo-fast-glow 3.2s ease-in-out infinite; transition: transform 0.15s ease; }
        .oltodo-fast-btn:hover { transform: scale(1.07); }
      `}</style>
      <button onClick={() => setShowFastCreate(true)} title={t(lang,"expl_create_fast")} className="oltodo-fast-btn" style={{
        position: "absolute", top: (showMembers || showMoves) ? 104 : 60, left: 14, zIndex: 1000,
        width: 44, height: 44, borderRadius: 22, cursor: "pointer",
        backgroundColor: "#0a0a0a", border: `1.5px solid ${FAST_COLOR}`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Glyph kind="fast" size={19} color={FAST_COLOR} />
      </button>

      {showFastCreate && (
        <FastCreateSheet
          onClose={() => setShowFastCreate(false)}
          onCreated={(fast) => {
            setFasts(prev => [fast, ...prev]);
            setShowFastCreate(false);
          }}
        />
      )}

      {/* "Rechercher dans cette zone" — apparaît dès qu'on déplace la carte suffisamment loin
          (ex: vers une autre ville, un autre pays), pour y découvrir profils/lieux/rituels sur
          place plutôt que de rester limité aux environs de sa propre position. */}
      {showSearchAreaButton && (
        <button onClick={searchThisArea} style={{
          position: "absolute", top: (showMembers || showMoves) ? 104 : 60, left: "50%", transform: "translateX(-50%)", zIndex: 1000,
          display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 20, border: "none", cursor: "pointer",
          backgroundColor: "#0a0a0a", color: "white", fontSize: 12.5, fontWeight: 700,
          boxShadow: "0 3px 12px rgba(0,0,0,0.25)",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          {t(lang,"explore_search_area")||"Rechercher dans cette zone"}
        </button>
      )}

      {/* Bouton ajouter un lieu */}
      <button onClick={() => setShowCreateVenue(true)} title="Ajouter un lieu" style={{
        position: "absolute", top: rightStackTop + 96, right: 14, zIndex: 1000,
        width: 40, height: 40, borderRadius: "50%", border: "none", cursor: "pointer",
        backgroundColor: "#0a0a0a", boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z"/>
          <path d="M12 8v5M9.5 10.5h5"/>
        </svg>
      </button>

      {showCreateVenue && (
        <CreateVenueModal
          onClose={() => setShowCreateVenue(false)}
          onCreated={v => setVenues(prev => [v, ...prev])}
        />
      )}

      {/* Détail d'un trajet MOVE — même modale que pages/Move.tsx, ouverte depuis un marqueur ou
          une carte de la fiche générique (voir renderDetailCard, item.kind === "move"). */}
      {moveDetailId && (
        <MoveDetailModal moveId={moveDetailId} onClose={() => setMoveDetailId(null)} />
      )}

      {/* Lecture d'un Live : la visionneuse plein écran déjà utilisée par la galerie de profil et
          le fil "Live" du Home — même lecteur, mêmes gestes, aucun player réinventé ici. */}
      {liveLightbox && (
        <PhotoLightbox
          photos={[{
            id: liveLightbox.id,
            url: liveLightbox.url,
            location: liveLightbox.location_name || null,
            likes_count: Number(liveLightbox.likes_count) || 0,
            liked_by_me: !!liveLightbox.liked_by_me,
            media_type: "video",
            views_count: Number(liveLightbox.views_count) || 0,
            user_id: liveLightbox.user_id,
            author_name: liveLightbox.author_name,
            author_avatar: liveLightbox.author_avatar,
          } as GalleryPhoto]}
          index={0}
          onIndexChange={() => {}}
          onClose={() => setLiveLightbox(null)}
          isOwner={liveLightbox.user_id === user?.id}
          ownerId={liveLightbox.user_id}
          ownerName={liveLightbox.author_name}
          ownerAvatar={liveLightbox.author_avatar}
          currentUserId={user?.id}
          onLikeToggled={(id, liked, count) => {
            setLives(prev => prev.map(l => l.id === id ? { ...l, liked_by_me: liked, likes_count: count } : l));
            setLiveLightbox((prev: any) => prev && prev.id === id ? { ...prev, liked_by_me: liked, likes_count: count } : prev);
          }}
          onDeleted={id => {
            setLives(prev => prev.filter(l => l.id !== id));
            setLiveLightbox(null);
            setDetailItem(prev => prev && prev.data.id === id ? null : prev);
          }}
          onLocationUpdated={(id, location) => setLives(prev => prev.map(l => l.id === id ? { ...l, location_name: location } : l))}
          onViewed={id => setLives(prev => prev.map(l => l.id === id ? { ...l, views_count: Number(l.views_count || 0) + 1 } : l))}
        />
      )}

      {/* CARTE */}
      <style>{`
        @keyframes oltodo-pulse {
          0% { transform: scale(0.6); opacity: 0.6; }
          70% { transform: scale(2.4); opacity: 0; }
          100% { transform: scale(2.4); opacity: 0; }
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }

        /* ————— PROFONDEUR : l'interprétation OLTODO de "la carte devient vivante quand on
           s'approche". Pas de nappe de chaleur colorée, pas d'animation par image : chaque
           contenu porte lui-même un halo sombre très diffus, et une ombre portée. Les deux sont
           NULS de loin (--oltodo-halo: 0) et se révèlent par paliers de zoom. Là où les contenus
           sont denses, les halos voisins se superposent et forment naturellement une zone plus
           "chaude" — la densité produit la lumière, on ne la peint pas par-dessus. Tout est en
           noir OLTODO (#0a0a0a) : aucune couleur n'entre dans le langage de la carte. */
        .oltodo-map { --oltodo-halo: 0; --oltodo-lift: 0 2px 6px rgba(0,0,0,0.20); --oltodo-drop: 0 1px 3px rgba(0,0,0,0.22); }
        .oltodo-map.map-zoom-mid { --oltodo-halo: 0.10; --oltodo-lift: 0 4px 12px rgba(0,0,0,0.24); --oltodo-drop: 0 3px 6px rgba(0,0,0,0.24); }
        .oltodo-map.map-zoom-close { --oltodo-halo: 0.17; --oltodo-lift: 0 8px 20px rgba(0,0,0,0.28); --oltodo-drop: 0 5px 10px rgba(0,0,0,0.26); }
        .oltodo-marker { position: relative; display: flex; align-items: center; justify-content: center; }
        .oltodo-marker > * { transition: box-shadow 0.45s ease, filter 0.45s ease; }
        .oltodo-marker::before {
          content: ""; position: absolute; inset: -34%; border-radius: 50%; pointer-events: none; z-index: -1;
          background: radial-gradient(circle at 50% 50%, rgba(10,10,10,0.62) 0%, rgba(10,10,10,0.26) 40%, rgba(10,10,10,0) 72%);
          opacity: var(--oltodo-halo, 0);
          transition: opacity 0.5s ease;
        }
        /* Respiration réservée au zoom le plus proche, et volontairement lente (7s) et de très
           faible amplitude : à l'échelle d'un seul marqueur elle est presque imperceptible, c'est
           l'ensemble légèrement désynchronisé (--oltodo-delay) qui donne l'impression que la zone
           est habitée. Jamais de clignotement, jamais de mouvement qui attire l'œil. */
        @keyframes oltodo-ambient {
          0%, 100% { transform: scale(1); opacity: var(--oltodo-halo, 0); }
          50% { transform: scale(1.09); opacity: calc(var(--oltodo-halo, 0) * 0.6); }
        }
        .map-zoom-close .oltodo-marker::before { animation: oltodo-ambient 7s ease-in-out infinite; animation-delay: var(--oltodo-delay, 0s); }
        @media (prefers-reduced-motion: reduce) {
          .map-zoom-close .oltodo-marker::before { animation: none; }
          .oltodo-marker > *, .oltodo-marker::before { transition: none; }
        }
      `}</style>
      {/* className volontairement statique : voir le commentaire sur MapDepthHandler juste
          au-dessus — react-leaflet ignore toute mise à jour ultérieure de cette prop, c'est
          MapDepthHandler qui applique réellement la classe de palier sur le DOM. */}
      <MapContainer center={[center.lat, center.lng]} zoom={12} className="oltodo-map map-zoom-far" style={{ width: "100%", height: "100%" }} zoomControl={false} ref={mapRef as any}>
        <ModernMapTiles />
        <MapClickHandler onClick={() => setSelectedId(null)} />
        <MapMoveHandler onMoveEnd={setPendingCenter} />
        <MapDepthHandler onBucket={onZoomBucket} />

        {/* Ma position en temps réel */}
        <Marker position={[center.lat, center.lng]} icon={myLocationIcon()} zIndexOffset={-1000} />

        {/* UNE seule couche pour tous les types : c'est ce qui permet à un amas de dire
            "23 profils, 8 lieux, 5 communautés" au lieu de laisser cinq amas mono-type se
            recouvrir au même endroit. Aucune couche de points d'intérêt externe n'est ajoutée :
            tout ce qui s'affiche ici vient de l'API OLTODO, et le fond de carte (CARTO
            light_all) est volontairement dépourvu de commerces/POI. */}
        <ClusterLayer
          points={mapPoints}
          getIcon={markerIcon}
          onMarkerClick={openDetail}
          clusterRadius={clusterRadius}
          deps={[selectedId, markerBaseSize]}
        />
      </MapContainer>

      {/* TIROIR DE DÉCOUVERTE — replié : carrousel mixte de tout ce qui est coché en haut.
          Un tap sur un marqueur (ou sur une carte du carrousel) ouvre d'abord la FICHE de cet
          élément-là, seule, et propose ensuite "Voir les autres …" pour dérouler sa catégorie.
          Déplié à la main sans rien avoir sélectionné : le menu des catégories, comme avant —
          c'est une autre porte d'entrée, tout aussi légitime, vers la navigation par liste. */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 1000,
        backgroundColor: "white",
        borderRadius: "20px 20px 0 0",
        boxShadow: sheetExpanded ? "0 -6px 28px rgba(0,0,0,0.16)" : "0 -4px 20px rgba(0,0,0,0.08)",
        height: sheetExpanded ? "64vh" : PEEK_HEIGHT,
        transition: "height 0.25s cubic-bezier(0.22,1,0.36,1)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        <div
          onClick={onHandleClick}
          onTouchStart={onHandleTouchStart}
          onTouchMove={onHandleTouchMove}
          onTouchEnd={onHandleTouchEnd}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "10px 0 6px", cursor: "pointer", flexShrink: 0, touchAction: "none" }}
        >
          <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: "#DDD" }} />
          <div style={{ fontSize: 12, color: "#999", marginTop: 7, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
            {loading ? (t(lang,"msg_loading")||"Chargement…") : `${drawerItems.length} ${t(lang,"explore_discover_suffix")||"à découvrir ici"}`}
            <span style={{ fontSize: 10 }}>{sheetExpanded ? "▾" : "▴"}</span>
          </div>
        </div>

        {/* Aucune catégorie cochée : la carte est vide et on le dit clairement, plutôt que de
            re-cocher "Tout" en douce dans le dos de l'utilisateur. */}
        {!loading && selected.size === 0 && (
          <div style={{ textAlign: "center", padding: "10px 24px 20px", color: "#999", fontSize: 13 }}>
            {t(lang,"explore_no_category_selected_web")||"Aucune catégorie sélectionnée, touchez « Tout » ou choisissez ce que vous voulez voir."}
          </div>
        )}

        {/* 1. Fiche d'un seul élément (tap sur un marqueur ou sur une carte du tiroir) */}
        {!loading && selected.size > 0 && detailItem && renderDetailCard(detailItem)}

        {/* 2. Tiroir replié : carrousel mixte de tout ce qui est coché */}
        {!loading && selected.size > 0 && !detailItem && !sheetExpanded && (
          peekMixed.length === 0 ? (
            <div style={{ textAlign: "center", padding: "10px 20px 20px", color: "#999", fontSize: 13 }}>{t(lang,"explore_nothing_here")||"Rien à découvrir ici pour le moment"}</div>
          ) : (
            <div className="no-scrollbar" style={{ display: "flex", gap: 12, overflowX: "auto", overflowY: "hidden", touchAction: "pan-x", padding: "0 14px 16px", flex: 1, alignItems: "center" }}>
              {peekMixed.map(renderPeekCard)}
            </div>
          )
        )}

        {/* 3. Liste complète d'une catégorie — ce qu'ouvre "Voir les autres …", et ce qu'ouvre
               aussi le menu des catégories. Exactement la même liste qu'avant : elle n'a pas
               disparu, elle a seulement cessé d'être ce qu'on reçoit sans l'avoir demandé. */}
        {!loading && selected.size > 0 && !detailItem && sheetExpanded && drilldownCategory !== null && (
          <div className="no-scrollbar" style={{ flex: 1, overflowY: "auto", padding: "0 14px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 4px" }}>
              <div onClick={() => setDrilldownCategory(null)} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#0a0a0a" }}>
                ← Retour
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5 }}>
                {categoryGroups.find(g => g.key === drilldownCategory)?.label}
              </div>
            </div>
            {categoryGroups.find(g => g.key === drilldownCategory)?.items.map(renderListRow)}
          </div>
        )}

        {/* 4. Tiroir déplié à la main, sans sélection : menu des catégories (ou la liste groupée
               directement quand une seule catégorie est cochée — un menu d'une seule entrée
               n'aurait aucun intérêt). */}
        {!loading && selected.size > 0 && !detailItem && sheetExpanded && drilldownCategory === null && (
          isSingleSelection ? (
            drawerItems.length === 0 ? (
              <div style={{ textAlign: "center", padding: "10px 20px 20px", color: "#999", fontSize: 13 }}>{t(lang,"explore_nothing_here")||"Rien à découvrir ici pour le moment"}</div>
            ) : (
              <div className="no-scrollbar" style={{ flex: 1, overflowY: "auto", padding: "0 14px 20px" }}>
                {Object.keys(groupedForExpanded).map(kind => (
                  <div key={kind} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5, padding: "10px 4px 2px" }}>
                      {KIND_LABELS[kind]} ({groupedForExpanded[kind].length})
                    </div>
                    {groupedForExpanded[kind].map(renderListRow)}
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="no-scrollbar" style={{ flex: 1, overflowY: "auto", padding: "4px 14px 20px" }}>
              {categoryGroups.map(g => (
                <div key={g.key} onClick={() => g.items.length > 0 && setDrilldownCategory(g.key)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 4px", borderBottom: "1px solid #F5F5F5", cursor: g.items.length > 0 ? "pointer" : "default", opacity: g.items.length > 0 ? 1 : 0.4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Glyph kind={GROUP_TO_KIND[g.key]} size={17} color="#0a0a0a" />
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{g.label}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, color: "#999" }}>{g.items.length}</span>
                    {g.items.length > 0 && <span style={{ fontSize: 16, color: "#ccc" }}>›</span>}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
